import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import VehicleCreateDialog from './VehicleCreateDialog';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  DocumentTextIcon,
  CheckIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { ChevronDownIcon, WrenchIcon, Droplets, Fuel, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import ClientCombobox from './ClientCombobox';
import ClientQuickPicks from './ClientQuickPicks';
import { Tip } from './ui/tip';
import { getAppSocket } from '@/lib/wesSocket';
import { hoyLocal, toYMD } from '../utils/fechaLocal';
import { itemsAprobados } from '../utils/budgetItems';
import { parseKm } from '../utils/km';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  phone: string;
  email: string;
  contactPerson?: string;
  contactPhone?: string;
  isFleet?: number;
  whatsapp?: string;
}

interface Vehicle {
  id: string;
  licensePlate: string;
  make: string;
  model: string;
  year?: number;
  clientId: string;
  contactName?: string;
  contactPhone?: string;
}



interface Budget {
  id: string;
  budgetNumber?: string;
  description: string;
  totalAmount?: number; // la lista lo manda como totalAmount; el detalle como total
  total?: number;
  status: string;
  clientId: string;
  vehicleId?: string;
  paymentMethod?: string;
  items?: BudgetItem[];
}

interface BudgetItem {
  id: string;
  name: string;
  qty: number;
  sellingPrice: number;
  type: string;
  duration?: number; // duración en minutos
  requiredRole?: string;
  requiredSkills?: string[];
  requiredStaff?: number;
  fromBudget?: boolean; // vino del presupuesto elegido (se reemplaza si se cambia de presupuesto)
}

const etiquetaPresupuesto = (b: Budget) => {
  const monto = Number(b.totalAmount ?? b.total ?? 0);
  const plata = isNaN(monto) ? 0 : monto;
  return `${b.budgetNumber ? `${b.budgetNumber} · ` : ''}${b.description || 'Sin descripción'} · $${plata.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

interface Service {
  id: string;
  name: string;
  duration: number; // duración en minutos
  requiredRole?: string;
  requiredSkills?: string[];
  requiredStaff?: number;
  salePrice: number; // cambio de price a salePrice
  category: string;
  description?: string;
  sku?: string;
}

interface AppointmentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  appointment?: any; // Para edición
  mode: 'create' | 'edit';
  asPage?: boolean;
  initialData?: {
    timeSlot?: string;
    scheduledDate?: string;
    clientId?: string;
    vehicleId?: string;
    budgetId?: string;
  };
  defaultImmediate?: boolean;
}

// DatePicker component matching shadcn/ui reference
const DatePickerAppointment = ({
  value,
  onChange,
  id,
  minDate,
  compact = false
}: {
  value?: string;
  onChange: (date: string) => void;
  id?: string;
  minDate?: string;
  compact?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  // Parsear la fecha correctamente para evitar problemas de zona horaria
  const dateValue = value ? (() => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : undefined;

  const minDateObj = minDate ? (() => {
    const [year, month, day] = minDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : new Date();

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Formatear la fecha correctamente en zona horaria local
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
      setOpen(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            compact
              ? "h-8 rounded-lg px-3 text-sm font-medium justify-start gap-2"
              : "w-full justify-between font-normal",
            !dateValue && "text-muted-foreground"
          )}
        >
          <CalendarIcon className={cn("opacity-50", compact ? "h-4 w-4" : "h-4 w-4 mr-2")} />
          {dateValue ? dateValue.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }) : (compact ? 'Programado' : 'Seleccionar fecha')}
          {!compact && <ChevronDownIcon className="h-4 w-4 opacity-50" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          disabled={(date) => {
            const dateOnly = new Date(date);
            dateOnly.setHours(0, 0, 0, 0);
            const minDateOnly = new Date(minDateObj);
            minDateOnly.setHours(0, 0, 0, 0);
            return dateOnly < minDateOnly;
          }}
          captionLayout="dropdown"
          fromYear={currentYear - 1}
          toYear={currentYear + 2}
        />
      </PopoverContent>
    </Popover>
  );
};

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  appointment,
  mode,
  asPage = false,
  initialData,
  defaultImmediate = false
}) => {
  const { isDarkMode } = useTheme();
  const currency = (v: number) => `$${Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const [clients, setClients] = useState<Client[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  // Buscador por patente: encuentra el vehiculo cargado y autocompleta cliente+vehiculo
  const [plateQuery, setPlateQuery] = useState('');
  const [plateSearching, setPlateSearching] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<string>('');
  // El presupuesto elegido, tal como lo devolvió la API: así figura en el
  // selector aunque la lista de aprobados del cliente todavía no lo traiga
  const [budgetElegido, setBudgetElegido] = useState<Budget | null>(null);
  const [selectedServices, setSelectedServices] = useState<BudgetItem[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerQuery, setProductPickerQuery] = useState('');
  const [productPickerHighlight, setProductPickerHighlight] = useState(0);
  const productPickerInputRef = useRef<HTMLInputElement>(null);
  const productPickerListRef = useRef<HTMLDivElement>(null);
  // Vehículo elegido antes de que cargue la lista del cliente (patente recién
  // resuelta): se re-aplica cuando llega la lista.
  const pendingVehicleIdRef = useRef<string | null>(null);
  // Indice combinado del item enfocado para navegacion con teclado.
  // 0 .. selectedServices.length-1 = servicios; el resto = productos.
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);
  const [selectedService, setSelectedService] = useState<string>('');
  const [serviceQuantity, setServiceQuantity] = useState<number>(1);
  const [editingQuantityIndex, setEditingQuantityIndex] = useState<number | null>(null);
  const [editingQuantityType, setEditingQuantityType] = useState<'service' | 'product' | null>(null);
  const [editingQuantityValue, setEditingQuantityValue] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [slotsWithInfo, setSlotsWithInfo] = useState<any[]>([]);
  // Semáforo completo: todos los horarios con su nivel de ocupación, sin bloquear
  const [slotsAll, setSlotsAll] = useState<Array<{ time: string; nivel: 'libre' | 'sobreturno' | 'lleno'; ocupados: number; capacidad: number }>>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [step, setStep] = useState(1);
  const [isImmediate, setIsImmediate] = useState(defaultImmediate);
  // Orden de estaciones
  const [stationOrder, setStationOrder] = useState<string[]>([]);
  const [availableStations] = useState([
    { key: 'taller', name: 'Taller' },
    { key: 'detailing', name: 'Detailing' },
    { key: 'lavadero', name: 'Lavadero' },
  ]);

  // Reclamo
  const [isReclamo, setIsReclamo] = useState(false);
  const [reclamoSourceSearch, setReclamoSourceSearch] = useState('');
  const [reclamoSourceResults, setReclamoSourceResults] = useState<any[]>([]);
  const [reclamoSourceSelected, setReclamoSourceSelected] = useState<{id: string; code: string} | null>(null);
  const [reclamoType, setReclamoType] = useState('');
  const [reclamoNotes, setReclamoNotes] = useState('');
  const [formPopulated, setFormPopulated] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedResources, setSelectedResources] = useState<string[]>(['taller']);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientForm, setClientForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });

  // Estado para contacto de recepción
  const [contactSource, setContactSource] = useState<'auto' | 'vehicle' | 'fleet' | 'client' | 'custom' | 'other'>('auto');
  // Contacto elegido de la lista "cargados en otros vehículos del cliente"
  const [otherContact, setOtherContact] = useState<{ name: string; phone: string } | null>(null);
  // Nombre, apellido y cargo por separado: con un solo campo no se puede
  // saludar por el nombre de pila ni saber si el que atiende decide o maneja.
  const [customContactName, setCustomContactName] = useState('');
  const [customContactLastName, setCustomContactLastName] = useState('');
  const [customContactRole, setCustomContactRole] = useState('');
  const [customContactPhone, setCustomContactPhone] = useState('');
  const nombreCompletoContacto = [customContactName, customContactLastName]
    .map((x) => String(x || '').trim()).filter(Boolean).join(' ');
  const [vehicleContactName, setVehicleContactName] = useState('');
  const [vehicleContactPhone, setVehicleContactPhone] = useState('');
  const [fleetContactName, setFleetContactName] = useState('');
  const [fleetContactPhone, setFleetContactPhone] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [savingVehicleContact, setSavingVehicleContact] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      clientId: '',
      vehicleId: '',
      budgetId: '',
      serviceType: 'Servicio general', // Valor por defecto
      scheduledDate: '',
      timeSlot: '',
      estimatedDuration: 60,


      notes: '',
      mileage: '',
      oilChangeAndFilter: false,
      specialInstructions: ''
    }
  });

  const watchedDate = watch('scheduledDate');
  const watchedClientId = watch('clientId');
  const watchedBudgetId = watch('budgetId');
  const watchedDuration = watch('estimatedDuration'); // Mantener para obtener la duración calculada

  // Duración estimada: se autocalcula sumando los servicios, PERO si el usuario
  // la edita a mano (durationTouchedRef) dejamos de pisarla. El auto-cálculo es
  // solo una sugerencia: varios servicios comparten pasos (ej. balanceo y
  // desarme usan el mismo sacar/poner rueda), así que suele sobrar y se ajusta.
  const durationTouchedRef = useRef(false);
  const applyAutoDuration = (list: any[]) => {
    if (durationTouchedRef.current) return;
    const total = (list || []).reduce((t: number, it: any) => t + (Number(it.duration) || 60) * (Number(it.qty) || 1), 0);
    setValue('estimatedDuration', Math.max(total, 30));
  };
  const recalcAutoDuration = () => { durationTouchedRef.current = false; applyAutoDuration(selectedServices); };
  const fmtDurationHM = (m: number) => {
    const n = Number(m) || 0; const h = Math.floor(n / 60); const mm = n % 60;
    return h ? `${h} h${mm ? ` ${mm} min` : ''}` : `${mm} min`;
  };
  // El input de duración usa estado local: si escribiera directo en el form,
  // cada tecla recargaba los horarios disponibles y el re-render remontaba el
  // input (se perdía el foco). Se confirma al salir del campo o con Enter.
  const [durationInput, setDurationInput] = useState<string>('');
  useEffect(() => {
    setDurationInput(watchedDuration == null ? '' : String(watchedDuration));
  }, [watchedDuration]);
  const commitDuration = () => {
    const n = parseInt(durationInput, 10);
    setValue('estimatedDuration', Number.isFinite(n) ? n : ('' as any));
  };

  // Cargar datos iniciales
  useEffect(() => {
    console.log('ðŸ” useEffect ejecutándose con:', { isOpen, mode, appointment, initialData });
    console.log('ðŸ” Estado actual del formulario:', {
      clientId: watch('clientId'),
      vehicleId: watch('vehicleId'),
      scheduledDate: watch('scheduledDate'),
      timeSlot: watch('timeSlot')
    });
    console.log('ðŸ” Valores anteriores vs nuevos:', {
      isOpen,
      mode,
      appointmentId: appointment?.id,
      appointmentClientId: appointment?.clientId,
      appointmentVehicleId: appointment?.vehicleId
    });

    if (isOpen) {
      console.log('ðŸš€ Modal abriéndose, cargando datos...');

      // Cargar datos básicos primero
      const loadBasicData = async () => {
        console.log('ðŸ”„ Iniciando carga de datos básicos...');
        await fetchClients();
        console.log('âœ… fetchClients completado');
        await fetchServices();
        console.log('âœ… fetchServices completado');
        await fetchProducts();
        console.log('âœ… fetchProducts completado');
        console.log('ðŸŽ¯ Todos los datos básicos cargados, procediendo con populateForm...');

        // Después de cargar los datos básicos, poblar el formulario si es necesario
        if (mode === 'edit' && appointment) {
          console.log('ðŸ“ Modo edición detectado, appointment:', appointment);
          console.log('ðŸ“ Campos del appointment:', {
            clientId: appointment.clientId,
            vehicleId: appointment.vehicleId,
            scheduledDate: appointment.scheduledDate,
            timeSlot: appointment.timeSlot,
            serviceType: appointment.serviceType,
            notes: appointment.notes,
            products: appointment.products
          });

          // Cargar vehículos del cliente antes de poblar el formulario
          if (appointment.clientId) {
            console.log('ðŸš— Cargando vehículos para cliente:', appointment.clientId);
            await fetchVehiclesByClient(appointment.clientId);
            console.log('âœ… fetchVehiclesByClient completado');
          }

          // Agregar un pequeño delay para asegurar que todos los datos estén cargados
          setTimeout(() => {
            console.log('ðŸš€ Ejecutando populateForm...');
            populateForm();
            console.log('âœ… populateForm ejecutado');
            console.log('ðŸ” Estado después de populateForm:', {
              selectedClient,
              clientId: watch('clientId'),
              vehicleId: watch('vehicleId'),
              scheduledDate: watch('scheduledDate'),
              timeSlot: watch('timeSlot')
            });
          }, 100);
        } else if (mode === 'create' && initialData) {
          console.log('âž• Modo creación detectado, initialData:', initialData);
          populateForm();
        } else {
          console.log('âš ️ No se detectó modo válido o datos faltantes');
          console.log('âš ️ mode:', mode, 'appointment:', !!appointment, 'initialData:', !!initialData);
        }
      };

      loadBasicData();
    } else {
      console.log('ðŸ”’ Modal cerrado, limpiando estado...');
      // Solo limpiar estado cuando se cierra el formulario y NO estamos editando
      if (mode !== 'edit') {
        setIsImmediate(false);
        setSelectedServices([]);
        setSelectedProducts([]);
        setSelectedService('');
        setServiceQuantity(1);
        setStep(1);
      }
      // Resetear flag de formulario populado
      setFormPopulated(false);
    }
  }, [isOpen, mode, appointment?.id, appointment?.clientId, appointment?.vehicleId, initialData]);

  // Efecto específico para cuando cambia el appointment (modo edición)
  useEffect(() => {
    if (mode === 'edit' && appointment && isOpen) {
      console.log('ðŸ”„ Appointment cambió, repoblando formulario...');
      console.log('ðŸ”„ Estado actual de selectedServices:', selectedServices);
      console.log('ðŸ”„ Estado actual de selectedProducts:', selectedProducts);

      // Esperar a que los vehículos estén cargados antes de poblar
      if (vehicles.length > 0 || !appointment.vehicleId) {
        console.log('ðŸš— Vehículos cargados, ejecutando populateForm...');
        populateForm();
      } else {
        // Si no hay vehículos cargados pero hay un vehicleId, cargar los vehículos primero
        if (appointment.clientId && appointment.vehicleId) {
          console.log('ðŸš— Cargando vehículos antes de populateForm...');
          fetchVehiclesByClient(appointment.clientId).then(() => {
            setTimeout(() => {
              console.log('ðŸš— Vehículos cargados, ejecutando populateForm con delay...');
              populateForm();
            }, 100);
          });
        }
      }
    }
  }, [appointment, mode, isOpen, vehicles.length]);

  // Efecto para asegurar que los servicios y productos se muestren correctamente
  useEffect(() => {
    if (mode === 'edit' && appointment && selectedServices.length === 0 && selectedProducts.length === 0) {
      // Si estamos editando pero no hay servicios/productos seleccionados, intentar cargarlos
      const allProducts = [
        ...(appointment.products || [])
      ];

      if (allProducts.length > 0) {
        const services = allProducts.filter((p: any) => p.type === 'service');
        const products = allProducts.filter((p: any) => p.type === 'product');

        if (services.length > 0) {
          const mappedServices = services.map((s: any) => ({
            id: s.id || `temp-${Date.now()}`,
            name: s.name,
            qty: s.quantity || 1,
            sellingPrice: s.price || 0,
            type: 'service',
            duration: s.duration || 60,
            requiredRole: s.requiredRole || '',
            requiredSkills: Array.isArray(s.requiredSkills)
              ? s.requiredSkills
              : (() => { try { return JSON.parse(s.requiredSkills || '[]'); } catch { return []; } })(),
            requiredStaff: s.requiredStaff || 1
          }));
          setSelectedServices(mappedServices);
        }

        if (products.length > 0) {
          const mappedProducts = products.map((p: any) => ({
            id: p.id || `temp-${Date.now()}`,
            name: p.name,
            qty: p.quantity || 1,
            sellingPrice: p.price || 0,
            type: 'product'
          }));
          setSelectedProducts(mappedProducts);
        }
      }
    }
  }, [mode, appointment, selectedServices.length, selectedProducts.length]);

  // Efecto para monitorear cambios en selectedServices y selectedProducts
  useEffect(() => {
    console.log('ðŸ”„ selectedServices cambió:', selectedServices);
    console.log('ðŸ”„ selectedProducts cambió:', selectedProducts);
  }, [selectedServices, selectedProducts]);

  // Efecto final para asegurar que el formulario esté completamente populado en modo edición
  useEffect(() => {
    if (mode === 'edit' && appointment && isOpen && !formPopulated) {
      // Verificar si todos los datos necesarios están cargados
      const hasAllData = appointment.clientId &&
        (appointment.vehicleId || vehicles.length === 0) &&
        vehicles.length > 0;

      if (hasAllData) {
        console.log('ðŸŽ¯ Todos los datos están cargados, populando formulario final...');
        populateForm();
        setFormPopulated(true);
      }
    }
  }, [mode, appointment, isOpen, formPopulated, vehicles.length]);

  // Efecto para manejar cambios de modo
  useEffect(() => {
    if (mode === 'edit') {
      setFormPopulated(false);
      console.log('ðŸ”„ Modo cambiado a edición, reseteando formPopulated');
    } else {
      // Modo creación: limpiar estado
      setFormPopulated(false);
      setSelectedServices([]);
      setSelectedProducts([]);
      setSelectedService('');
      setServiceQuantity(1);
      setStep(1);
      console.log('ðŸ”„ Modo cambiado a creación, limpiando estado');
    }
  }, [mode]);

  // Efecto adicional para asegurar carga de servicios en modo edición
  useEffect(() => {
    if (mode === 'edit' && appointment && isOpen) {
      console.log('ðŸ”„ Efecto adicional para modo edición ejecutándose...');
      console.log('ðŸ”„ appointment:', appointment);
      console.log('ðŸ”„ selectedServices actual:', selectedServices);
      console.log('ðŸ”„ selectedProducts actual:', selectedProducts);

      // Si no hay servicios cargados, intentar cargarlos nuevamente
      if (selectedServices.length === 0 && selectedProducts.length === 0) {
        console.log('ðŸ”„ No hay servicios/productos cargados, ejecutando populateForm...');
        setTimeout(() => {
          populateForm();
        }, 200);
      }
    }
  }, [mode, appointment, isOpen, selectedServices.length, selectedProducts.length]);

  // El flujo "Cliente sin datos (solo patente)" vive en ClientQuickPicks

  // El vehiculo suele elegirse ANTES de que termine de cargar la lista del
  // cliente (viene de un presupuesto, de una patente recien resuelta o de un
  // vehiculo recien creado). Cuando la lista llega, se re-aplica el valor para
  // que el selector lo muestre; si no, queda en "Seleccionar vehiculo...".
  useEffect(() => {
    const actual = watch('vehicleId') || pendingVehicleIdRef.current;
    if (actual && vehicles.some((v) => v.id === actual)) {
      setValue('vehicleId', actual, { shouldValidate: true, shouldDirty: true });
      pendingVehicleIdRef.current = null;
      return;
    }
    // Alta de turno con un cliente que tiene un solo auto y nada elegido: se
    // elige solo (es el caso del cliente "Sin datos" recién creado por patente).
    if (mode !== 'edit' && !watch('vehicleId') && vehicles.length === 1) {
      setValue('vehicleId', vehicles[0].id, { shouldValidate: true, shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  // Cargar vehículos cuando cambia el cliente
  useEffect(() => {
    if (watchedClientId) {
      // Envolver las llamadas en try-catch para evitar que errores rompan la aplicación
      const loadClientData = async () => {
        try {
          await Promise.all([
            fetchVehiclesByClient(watchedClientId),
            fetchBudgetsByClient(watchedClientId)
          ]);
        } catch (error) {
          console.error('âŒ Error al cargar datos del cliente:', error);
          // Asegurarse de que los estados estén limpios en caso de error
          setVehicles([]);
          setBudgets([]);
        }
      };
      
      loadClientData();
      
      // Solo establecer selectedClient si NO estamos en modo de edición
      // para evitar que se sobrescriba el valor establecido por populateForm
      if (mode !== 'edit') {
        setSelectedClient(watchedClientId);
      }
    } else {
      // Limpiar vehículos cuando no hay cliente seleccionado
      setVehicles([]);
      setBudgets([]);
      // Solo limpiar selectedClient si NO estamos en modo de edición
      if (mode !== 'edit') {
        setSelectedClient('');
      }
      setValue('vehicleId', ''); // Limpiar selección de vehículo en el formulario
      setValue('budgetId', ''); // Limpiar selección de presupuesto
    }
  }, [watchedClientId, setValue, mode]);

  // Manejar selección de presupuesto
  useEffect(() => {
    // En modo edición no tocamos servicios/productos ni valores derivados del presupuesto
    if (mode === 'edit') {
      return;
    }
    if (watchedBudgetId) {
      setSelectedBudget(watchedBudgetId);
      aplicarPresupuesto(watchedBudgetId);
    } else {
      // Sin presupuesto: se van sus renglones (lo cargado a mano se queda)
      setSelectedBudget('');
      setBudgetElegido(null);
      setSelectedServices((prev) => prev.filter((s) => !s.fromBudget));
      setSelectedProducts((prev) => prev.filter((p) => !p.fromBudget));
      setValue('estimatedDuration', 60);
      setValue('serviceType', 'Servicio general'); // Resetear serviceType
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedBudgetId, setValue, mode]);

  // Cargar slots disponibles cuando cambia fecha Y ya hay duración calculada
  useEffect(() => {
    if (watchedDate && watchedDuration && watchedDuration > 0 && selectedResources.length > 0) {
      fetchAvailableSlots(watchedDate, watchedDuration);
    } else if (watchedDate && selectedResources.length > 0) {
      // Si hay fecha pero no duración, usar 60 min por defecto para mostrar slots
      fetchAvailableSlots(watchedDate, 60);
    }
  }, [watchedDate, watchedDuration, selectedResources]);

  // Restaurar foco en el input del buscador después de cada keystroke o cambio de highlight
  // (workaround a que el componente padre re-renderiza y el input pierde foco)
  useEffect(() => {
    if (productPickerOpen && productPickerInputRef.current) {
      const el = productPickerInputRef.current;
      if (document.activeElement !== el) {
        el.focus();
        // Posicionar caret al final
        const len = el.value.length;
        try { el.setSelectionRange(len, len); } catch {}
      }
    }
  }, [productPickerQuery, productPickerOpen, productPickerHighlight]);

  // Reset highlight cuando cambia el query del picker
  useEffect(() => {
    setProductPickerHighlight(0);
  }, [productPickerQuery, productPickerOpen]);

  const fetchClients = async () => {
    try {
      console.log('ðŸ‘¥ Cargando clientes...');
      const response = await axios.get('/api/clients?limit=10000');
      console.log('ðŸ‘¥ Respuesta de clientes:', response.data);
      setClients(response.data.clients || []);
      console.log('ðŸ‘¥ Clientes cargados:', response.data.clients?.length || 0);
    } catch (error) {
      console.error('âŒ Error loading clients:', error);
    }
  };



  const fetchVehiclesByClient = async (clientId: string) => {
    setVehiclesLoading(true);
    try {
      console.log('ðŸš— Cargando vehículos para cliente:', clientId);

      // Intentar con URL absoluta primero
      let response;
      try {
        response = await axios.get(`/api/vehicles?clientId=${clientId}`);
        console.log('ðŸš— Respuesta con URL absoluta:', response.data);
      } catch (absoluteError: any) {
        console.log('âš ️ Error con URL absoluta, intentando con proxy:', absoluteError?.message || 'Error desconocido');
        response = await axios.get(`/api/vehicles?clientId=${clientId}`);
        console.log('ðŸš— Respuesta con proxy:', response.data);
      }

      // Robust handling of response data
      const vehiclesData = response.data;
      if (Array.isArray(vehiclesData)) {
        setVehicles(vehiclesData);
        console.log('ðŸš— Vehículos cargados (array directo):', vehiclesData.length);
      } else if (vehiclesData && Array.isArray(vehiclesData.vehicles)) {
        setVehicles(vehiclesData.vehicles);
        console.log('ðŸš— Vehículos cargados (propiedad vehicles):', vehiclesData.vehicles.length);
      } else {
        console.warn('âš ️ La respuesta de vehículos no es un array válido:', vehiclesData);
        setVehicles([]);
      }
    } catch (error: any) {
      console.error('âŒ Error loading vehicles:', error);
      console.error('âŒ Error details:', error?.response?.status, error?.response?.data);
      setVehicles([]); // Limpiar vehículos en caso de error
    } finally {
      setVehiclesLoading(false);
    }
  };

  useEffect(() => {
    const clientId = watchedClientId;
    if (!isOpen) return;
    const socket = getAppSocket();

    const handleVehiclesUpdate = (payload: any) => {
      if (!clientId) return;
      if (payload?.clientId && payload.clientId !== clientId) return;
      fetchVehiclesByClient(clientId);
    };

    socket.on('vehicles:update', handleVehiclesUpdate);
    return () => {
      socket.off('vehicles:update', handleVehiclesUpdate);
    };
  }, [isOpen, watchedClientId]);

  // Actualizar contactos disponibles cuando cambia el vehículo o el cliente
  const watchedVehicleId = watch('vehicleId');

  // Contactos de recepción disponibles (vehículo / flota / cliente). Si hay un solo
  // número distinto, no tiene sentido mostrar la elección: se muestra ese contacto
  // y solo la opción "Otro contacto".
  const receptionContact = useMemo(() => {
    const list = [
      vehicleContactPhone ? { key: 'vehicle', name: vehicleContactName || 'Operario del vehículo', phone: vehicleContactPhone } : null,
      fleetContactPhone ? { key: 'fleet', name: fleetContactName || 'Contacto de flota', phone: fleetContactPhone } : null,
      clientPhone ? {
        key: 'client',
        name: (() => {
          const c = clients.find((c) => c.id === watchedClientId);
          return c?.companyName || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || 'Cliente';
        })(),
        phone: clientPhone,
      } : null,
    ].filter(Boolean) as Array<{ key: string; name: string; phone: string }>;
    const distinct = new Set(list.map((c) => String(c.phone).replace(/\D/g, '')));
    return { list, single: distinct.size <= 1 ? (list[0] || null) : null };
  }, [vehicleContactPhone, vehicleContactName, fleetContactPhone, fleetContactName, clientPhone, clients, watchedClientId]);

  // Contactos YA CARGADOS en otros vehículos del mismo cliente (el último
  // contacto usado en cada auto queda guardado en su ficha): se ofrecen para
  // elegir en vez de tipearlos de nuevo. Se filtran teléfonos repetidos.
  const otherVehicleContacts = useMemo(() => {
    const norm = (p: any) => String(p || '').replace(/\D/g, '');
    const propios = new Set([vehicleContactPhone, fleetContactPhone, clientPhone].map(norm).filter(Boolean));
    const vistos = new Set<string>();
    const out: Array<{ name: string; phone: string; plate: string }> = [];
    for (const v of vehicles) {
      if (v.id === watchedVehicleId) continue;
      const phone = (v as any)?.contactPhone || '';
      const n = norm(phone);
      if (!n || propios.has(n) || vistos.has(n)) continue;
      vistos.add(n);
      out.push({
        name: [(v as any)?.contactName, (v as any)?.contactLastName].filter(Boolean).join(' ') || 'Contacto',
        phone,
        plate: v.licensePlate || ''
      });
    }
    return out;
  }, [vehicles, watchedVehicleId, vehicleContactPhone, fleetContactPhone, clientPhone]);

  // Detectar estaciones automáticamente cuando cambian los servicios/productos
  useEffect(() => {
    const STATION_KEYWORDS: Record<string, string[]> = {
      taller: ['alineac', 'balanceo', 'freno', 'amort', 'mecan', 'gomeria', 'lubric', 'diagnos', 'suspension', 'aceite', 'filtro', 'cambio'],
      detailing: ['detailing', 'pulido', 'ceramica', 'estetica', 'optica', 'restaurac'],
      lavadero: ['lavado', 'lavadero'],
    };
    const DEFAULT_ORDER = ['taller', 'detailing', 'lavadero'];
    const names = [
      ...selectedServices.map(s => (s.name || '').toLowerCase()),
      ...selectedProducts.map(p => (p.name || '').toLowerCase()),
    ];
    const detected = new Set<string>();
    for (const [station, keywords] of Object.entries(STATION_KEYWORDS)) {
      if (names.some(n => keywords.some(k => n.includes(k)))) detected.add(station);
    }
    if (detected.size === 0 && names.length > 0) detected.add('taller');
    const newOrder = DEFAULT_ORDER.filter(s => detected.has(s));
    if (newOrder.length > 0) setStationOrder(newOrder);
  }, [selectedServices, selectedProducts]);

  useEffect(() => {
    // Contacto del vehículo seleccionado
    const vehicle = vehicles.find(v => v.id === watchedVehicleId);
    setVehicleContactName([vehicle?.contactName, (vehicle as any)?.contactLastName].filter(Boolean).join(' '));
    setVehicleContactPhone(vehicle?.contactPhone || '');

    // Contacto del cliente (flota / directo)
    const client = clients.find(c => c.id === watchedClientId);
    setClientPhone(client?.whatsapp || client?.phone || '');
    setFleetContactName(client?.contactPerson || '');
    setFleetContactPhone(client?.contactPhone || '');

    // Resetear selección de contacto a auto cuando cambia el vehículo
    if (mode !== 'edit') {
      setContactSource('auto');
      setCustomContactName('');
      setCustomContactPhone('');
    }
  }, [watchedVehicleId, watchedClientId, vehicles, clients]);


  const handleCreateClient = async () => {
    const firstName = String(clientForm.firstName || '').trim();
    const lastName = String(clientForm.lastName || '').trim();
    const phone = String(clientForm.phone || '').trim();
    const email = String(clientForm.email || '').trim();

    if (!firstName) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      setClientSaving(true);
      const res = await axios.post('/api/clients', {
        type: 'individual',
        firstName,
        lastName: lastName || null,
        phone: phone || null,
        email: email || null
      });

      const created = res.data?.client || res.data;
      await fetchClients();

      if (created?.id) {
        setValue('clientId', created.id);
        setSelectedClient(created.id);
      }

      setClientModalOpen(false);
      setClientForm({ firstName: '', lastName: '', phone: '', email: '' });
      toast.success('Cliente creado');
    } catch (error: any) {
      console.error('Error creating client:', error);
      toast.error(error.response?.data?.error || 'Error al crear el cliente');
    } finally {
      setClientSaving(false);
    }
  };

  const fetchBudgetsByClient = async (clientId: string) => {
    try {
      console.log('ðŸ“‹ Cargando presupuestos para cliente:', clientId);
      const response = await axios.get(`/api/budgets?clientId=${clientId}&status=approved`);

      // Robust handling of response data
      const budgetsData = response.data;
      if (budgetsData && Array.isArray(budgetsData.budgets)) {
        setBudgets(budgetsData.budgets);
        console.log('ðŸ“‹ Presupuestos cargados (propiedad budgets):', budgetsData.budgets.length);
      } else if (Array.isArray(budgetsData)) {
        setBudgets(budgetsData);
        console.log('ðŸ“‹ Presupuestos cargados (array directo):', budgetsData.length);
      } else {
        console.warn('âš ️ La respuesta de presupuestos no tiene el formato esperado:', budgetsData);
        setBudgets([]);
      }
    } catch (error) {
      console.error('âŒ Error loading budgets:', error);
      setBudgets([]);
    }
  };

  const fetchServices = async () => {
    try {
      console.log('ðŸ”§ Cargando servicios...');
      const response = await axios.get('/api/products?type=service&limit=all');
      const servicesData = response.data.products?.map((product: any) => ({
        id: product.id,
        name: product.name,
        duration: product.duration || 60,
        salePrice: product.salePrice || 0,
        category: product.category,
        description: product.description,
        sku: product.sku,
        requiredRole: product.requiredRole || '',
        requiredSkills: Array.isArray(product.requiredSkills)
          ? product.requiredSkills
          : (() => { try { return JSON.parse(product.requiredSkills || '[]'); } catch { return []; } })(),
        requiredStaff: product.requiredStaff || 1
      })) || [];

      setServices(servicesData);
      console.log('ðŸ”§ Servicios cargados:', servicesData.length);
    } catch (error) {
      console.error('âŒ Error loading services:', error);
      setServices([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get('/api/products?type=product&limit=all');
      const list = (res.data.products || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        sellingPrice: p.salePrice || p.sellingPrice || 0,
        location: p.location || '',
        stock: typeof p.stock !== 'undefined' ? p.stock : p.currentStock
      }));
      setCatalogProducts(list);
    } catch (e) {
      setCatalogProducts([]);
    }
  };

  // Vuelca un presupuesto en el turno: renglones aprobados (servicios y
  // productos), duración, tipo de servicio y vehículo. Es el único camino, lo
  // use el alta desde Presupuestos (viene budgetId en la URL) o el selector
  // "Presupuesto" del formulario: los dos cargan lo mismo y el turno queda
  // enlazado (relatedBudgetId sale de budgetId al guardar).
  const aplicarPresupuesto = async (budgetId: string) => {
    try {
      const response = await axios.get(`/api/budgets/${budgetId}`);
      const presupuesto = response.data || {};
      // Solo lo que el cliente aprobó: la base menos lo que rechazó, más la
      // opción elegida (las descartadas no van al turno).
      const allItems = Array.isArray(presupuesto.items) ? presupuesto.items : [];
      const aprobados = itemsAprobados<any>(allItems, presupuesto.selectedOption);
      const services: BudgetItem[] = [];
      const products: any[] = [];
      aprobados.forEach((it: any) => {
        const t = String(it.type || '').toLowerCase();
        const isService = t === 'service' || t === 'servicio';
        const item: BudgetItem = {
          id: it.productId || it.id || `tmp-${Math.random()}`,
          name: it.description || it.name || 'Item',
          qty: Number(it.quantity || it.qty || 1),
          sellingPrice: Number(it.sellingPrice || it.unitPrice || 0),
          type: isService ? 'service' : 'product',
          duration: Number(it.duration) || 60,
          fromBudget: true
        };
        if (isService) services.push(item);
        else products.push(item);
      });

      // Reemplaza lo que hubiera venido de otro presupuesto; lo cargado a mano se queda
      setSelectedServices((prev) => [...prev.filter((s) => !s.fromBudget), ...services]);
      setSelectedProducts((prev) => [...prev.filter((p) => !p.fromBudget), ...products]);

      const totalDuration = services.reduce((total, item) => total + (item.duration || 60) * item.qty, 0);
      if (!durationTouchedRef.current) setValue('estimatedDuration', Math.max(totalDuration, 30)); // mínimo 30 minutos
      setValue('serviceType', services.map((item) => item.name).join(', ') || 'Servicio del presupuesto');

      // El vehículo del presupuesto, si el turno todavía no tiene uno (la lista
      // del cliente puede llegar después: el efecto de vehicles lo re-aplica)
      if (presupuesto.vehicleId && !watch('vehicleId')) {
        pendingVehicleIdRef.current = presupuesto.vehicleId;
        setValue('vehicleId', presupuesto.vehicleId, { shouldValidate: true, shouldDirty: true });
      }

      setBudgetElegido({
        id: presupuesto.id || budgetId,
        budgetNumber: presupuesto.budgetNumber,
        description: presupuesto.description,
        total: presupuesto.total,
        status: presupuesto.status,
        clientId: presupuesto.clientId,
        vehicleId: presupuesto.vehicleId || undefined
      });
      if (services.length + products.length > 0) {
        toast.success(`Se cargaron ${services.length + products.length} renglones del presupuesto${presupuesto.budgetNumber ? ` ${presupuesto.budgetNumber}` : ''}`);
      }
    } catch (error) {
      console.error('Error cargando el presupuesto:', error);
      toast.error('No se pudo cargar el presupuesto');
    }
  };

  const addManualService = () => {
    if (!selectedService || serviceQuantity < 1) {
      toast.error('Selecciona un item y especifica la cantidad');
      return;
    }

    // Detectar si es un producto o servicio
    if (selectedService.startsWith('prod-')) {
      // Es un producto
      const productId = selectedService.replace('prod-', '');
      const product = catalogProducts.find(p => p.id === productId);

      if (!product) {
        toast.error('Producto no encontrado');
        return;
      }

      // Verificar si el producto ya existe para sumar cantidades
      const existingProductIndex = selectedProducts.findIndex(p => p.id === product.id);

      if (existingProductIndex >= 0) {
        // Producto ya existe, sumar cantidad
        const existingProduct = selectedProducts[existingProductIndex];
        const updatedProducts = [...selectedProducts];
        updatedProducts[existingProductIndex] = {
          ...existingProduct,
          qty: existingProduct.qty + serviceQuantity
        };

        setSelectedProducts(updatedProducts);
        console.log(`âœ… Producto existente actualizado: ${product.name}, cantidad total: ${updatedProducts[existingProductIndex].qty}`);
      } else {
        // Producto nuevo, agregarlo
        const newProduct = {
          id: product.id,
          name: product.name,
          qty: serviceQuantity,
          sellingPrice: product.sellingPrice || product.salePrice || 0,
          type: 'product',
          location: product.location || '',
          stock: typeof product.stock !== 'undefined' ? product.stock : undefined
        };

        console.log('ðŸ“¦ Nuevo producto a agregar:', newProduct);
        const updatedProducts = [...selectedProducts, newProduct];
        setSelectedProducts(updatedProducts);
      }

      console.log('ðŸ“‹ Productos actualizados:', selectedProducts.length);

      // Limpiar selección
      setSelectedService('');
      setServiceQuantity(1);

      console.log('âœ… Producto agregado exitosamente');
      console.log('   - selectedProducts actual:', selectedProducts.length);

      // Mostrar mensaje de confirmación
      toast.success(`Producto "${product.name}" agregado exitosamente`);
      return;
    }

    // Es un servicio
    const service = services.find(s => s.id === selectedService);
    if (!service) {
      toast.error('Servicio no encontrado');
      return;
    }

    // Verificar si el servicio ya existe para sumar cantidades
    const existingServiceIndex = selectedServices.findIndex(s => s.id === service.id);

    if (existingServiceIndex >= 0) {
      // Servicio ya existe, sumar cantidad
      const existingService = selectedServices[existingServiceIndex];
      const updatedServices = [...selectedServices];
      updatedServices[existingServiceIndex] = {
        ...existingService,
        qty: existingService.qty + serviceQuantity
      };

      setSelectedServices(updatedServices);
      console.log(`âœ… Servicio existente actualizado: ${service.name}, cantidad total: ${updatedServices[existingServiceIndex].qty}`);
    } else {
      // Servicio nuevo, agregarlo
      const newService = {
        id: service.id,
        name: service.name,
        qty: serviceQuantity,
        sellingPrice: service.salePrice,
        type: 'service',
        duration: service.duration,
        requiredRole: service.requiredRole || '',
        requiredSkills: service.requiredSkills || [],
        requiredStaff: service.requiredStaff || 1
      };

      console.log('ðŸ“¦ Nuevo servicio a agregar:', newService);
      const updatedServices = [...selectedServices, newService];
      setSelectedServices(updatedServices);
    }

    console.log('ðŸ“‹ Servicios actualizados:', selectedServices.length);

    // Obtener servicios actualizados para cálculos
    const currentServices = selectedServices.findIndex(s => s.id === service.id) >= 0
      ? selectedServices.map(s => s.id === service.id ? { ...s, qty: s.qty + serviceQuantity } : s)
      : [...selectedServices, { id: service.id, name: service.name, qty: serviceQuantity, sellingPrice: service.salePrice, type: 'service', duration: service.duration, requiredRole: service.requiredRole || '', requiredSkills: service.requiredSkills || [], requiredStaff: service.requiredStaff || 1 }];

    // Actualizar duración total
    const newTotalDuration = currentServices.reduce((total, item) => {
      return total + (item.duration || 60) * item.qty;
    }, 0);
    if (!durationTouchedRef.current) setValue('estimatedDuration', Math.max(newTotalDuration, 30));

    // Actualizar serviceType automáticamente
    const serviceNames = currentServices.map(s => s.name).join(', ');
    setValue('serviceType', serviceNames || 'Servicio general');

    // Limpiar selección
    setSelectedService('');
    setServiceQuantity(1);

    console.log('âœ… Servicio agregado exitosamente');
    console.log('   - selectedServices actual:', selectedServices.length);
    console.log('   - serviceType actualizado:', serviceNames);
    console.log('   - duración total:', newTotalDuration);

    // Mostrar mensaje de confirmación
    toast.success(`Servicio "${service.name}" agregado exitosamente`);
  };

  const removeManualService = (index: number) => {
    const newServices = selectedServices.filter((_, i) => i !== index);
    setSelectedServices(newServices);

    // Actualizar duración total
    const newTotalDuration = newServices.reduce((total, item) => {
      return total + (item.duration || 60) * item.qty;
    }, 0);
    if (!durationTouchedRef.current) setValue('estimatedDuration', Math.max(newTotalDuration, 30));

    // Actualizar serviceType automáticamente
    const serviceNames = newServices.map(s => s.name).join(', ');
    setValue('serviceType', serviceNames || 'Servicio general');
  };

  const startEditingQuantity = (index: number, type: 'service' | 'product', currentQty: number) => {
    setEditingQuantityIndex(index);
    setEditingQuantityType(type);
    setEditingQuantityValue(currentQty.toString());
  };

  const saveQuantity = (index: number, type: 'service' | 'product') => {
    const qty = parseInt(editingQuantityValue);
    if (isNaN(qty) || qty < 1) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    if (type === 'service') {
      const updatedServices = [...selectedServices];
      updatedServices[index] = { ...updatedServices[index], qty };
      setSelectedServices(updatedServices);

      // Actualizar duración total
      const newTotalDuration = updatedServices.reduce((total, item) => {
        return total + (item.duration || 60) * item.qty;
      }, 0);
      if (!durationTouchedRef.current) setValue('estimatedDuration', Math.max(newTotalDuration, 30));
    } else {
      const updatedProducts = [...selectedProducts];
      updatedProducts[index] = { ...updatedProducts[index], qty };
      setSelectedProducts(updatedProducts);
    }

    setEditingQuantityIndex(null);
    setEditingQuantityType(null);
    setEditingQuantityValue('');
  };

  const cancelEditingQuantity = () => {
    setEditingQuantityIndex(null);
    setEditingQuantityType(null);
    setEditingQuantityValue('');
  };

  // Atajos de teclado para el step 2 (productos/servicios):
  //   .   -> abre el buscador y enfoca el input
  //   ↑/↓ -> mueve el item enfocado en la tabla
  //   *   -> edita la cantidad del item enfocado
  useEffect(() => {
    if (step !== 2 || selectedBudget) return;

    const totalItems = selectedServices.length + selectedProducts.length;
    const isEditableTarget = (target: EventTarget | null) => {
      if (!target) return false;
      const el = target as HTMLElement;
      return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Punto: abrir buscador
      if (e.key === '.') {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        setProductPickerOpen(true);
        return;
      }

      // Flechas: navegar items (solo si hay items y no estamos editando)
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && totalItems > 0 && editingQuantityIndex === null) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        setFocusedItemIndex((prev) => {
          if (prev < 0) return 0;
          if (e.key === 'ArrowDown') return Math.min(totalItems - 1, prev + 1);
          return Math.max(0, prev - 1);
        });
        return;
      }

      // Asterisco: editar cantidad del item enfocado (o el primero si no hay foco)
      if (e.key === '*') {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        const idx = focusedItemIndex >= 0 ? focusedItemIndex : 0;
        if (idx < selectedServices.length) {
          startEditingQuantity(idx, 'service', selectedServices[idx].qty);
        } else {
          const productIdx = idx - selectedServices.length;
          if (selectedProducts[productIdx]) {
            startEditingQuantity(productIdx, 'product', selectedProducts[productIdx].qty);
          }
        }
        return;
      }

      // Supr / Delete: elimina el item enfocado
      if ((e.key === 'Delete' || e.key === 'Supr') && totalItems > 0 && editingQuantityIndex === null) {
        if (isEditableTarget(e.target)) return;
        if (focusedItemIndex < 0) return;
        e.preventDefault();
        const idx = focusedItemIndex;
        if (idx < selectedServices.length) {
          // Borrar servicio
          const removed = selectedServices[idx];
          const updated = selectedServices.filter((_, i) => i !== idx);
          setSelectedServices(updated);
          const totalDur = updated.reduce((t, it) => t + (it.duration || 60) * it.qty, 0);
          if (!durationTouchedRef.current) setValue('estimatedDuration', Math.max(totalDur, 30));
          setValue('serviceType', updated.map(s => s.name).join(', ') || 'Servicio general');
          if (removed) toast.success(`"${removed.name}" eliminado`);
        } else {
          // Borrar producto
          const productIdx = idx - selectedServices.length;
          const removed = selectedProducts[productIdx];
          setSelectedProducts((prev) => prev.filter((_, i) => i !== productIdx));
          if (removed) toast.success(`"${removed.name}" eliminado`);
        }
        // Reajustar foco al item siguiente o anterior si hay
        setFocusedItemIndex((prev) => {
          const newTotal = totalItems - 1;
          if (newTotal <= 0) return -1;
          return Math.min(prev, newTotal - 1);
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, selectedBudget, selectedServices, selectedProducts, focusedItemIndex, editingQuantityIndex]);

  const fetchAvailableSlots = async (date: string, duration: number) => {
    console.log('ðŸ” Fetching slots para fecha:', date, 'duración:', duration, 'recursos:', selectedResources);
    setLoadingSlots(true);
    try {
      // Enviar recursos seleccionados como parámetro
      const resourcesParam = selectedResources.join(',');
      const url = `/api/appointments-enhanced/available-slots/${date}?duration=${duration}&resources=${resourcesParam}`;
      console.log('ðŸ“¡ URL:', url);
      const response = await axios.get(url);
      console.log('âœ… Respuesta slots:', response.data);
      setAvailableSlots(response.data.availableSlots || []);
      setSlotsWithInfo(response.data.slotsWithInfo || []);
      setSlotsAll(response.data.slotsAll || []);
    } catch (error) {
      console.error('âŒ Error loading available slots:', error);
      setAvailableSlots([]);
      setSlotsWithInfo([]);
      setSlotsAll([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const populateForm = () => {
    console.log('ðŸ“ Populando formulario...');
    if (mode === 'edit' && appointment) {
      console.log('ðŸ”§ Modo edición, appointment:', appointment);
      console.log('ðŸ” Estructura completa del appointment:', JSON.stringify(appointment, null, 2));

      // Mapear campos del appointment a los campos del formulario
      if (appointment.clientId) {
        console.log('ðŸ‘¥ Estableciendo clientId:', appointment.clientId);
        setValue('clientId', appointment.clientId);
        setSelectedClient(appointment.clientId);
        console.log('âœ… clientId establecido');
        console.log('ðŸ‘¥ selectedClient después de setSelectedClient:', appointment.clientId);
      } else {
        console.log('âš ️ No hay clientId en el appointment');
      }

      // El presupuesto enlazado se muestra en el selector (solo lectura): el
      // vínculo se cambia desde Presupuestos, no desde acá
      if (appointment.relatedBudgetId && watch('budgetId') !== appointment.relatedBudgetId) {
        setValue('budgetId', appointment.relatedBudgetId);
        axios.get(`/api/budgets/${appointment.relatedBudgetId}`)
          .then((r) => setBudgetElegido({
            id: r.data.id, budgetNumber: r.data.budgetNumber, description: r.data.description,
            total: r.data.total, status: r.data.status, clientId: r.data.clientId, vehicleId: r.data.vehicleId || undefined
          }))
          .catch(() => {});
      }

      if (appointment.vehicleId) {
        console.log('ðŸš— Estableciendo vehicleId:', appointment.vehicleId);
        setValue('vehicleId', appointment.vehicleId);
        console.log('âœ… vehicleId establecido');
      } else {
        console.log('âš ️ No hay vehicleId en el appointment');
      }

      if (appointment.scheduledDate) {
        console.log('ðŸ“… Estableciendo scheduledDate:', appointment.scheduledDate);
        const date = new Date(appointment.scheduledDate);
        const formattedDate = toYMD(date);
        setValue('scheduledDate', formattedDate);
        console.log('âœ… scheduledDate establecido:', formattedDate);
      } else {
        console.log('âš ️ No hay scheduledDate en el appointment');
      }

      if (appointment.timeSlot) {
        console.log('⏰ Estableciendo timeSlot:', appointment.timeSlot);
        setValue('timeSlot', appointment.timeSlot);
        console.log('âœ… timeSlot establecido');
      } else {
        console.log('âš ️ No hay timeSlot en el appointment');
      }

      if (appointment.estimatedDuration) {
        console.log('⏱️ Estableciendo estimatedDuration:', appointment.estimatedDuration);
        durationTouchedRef.current = true; // respetar la duración guardada del turno
        setValue('estimatedDuration', appointment.estimatedDuration);
        console.log('âœ… estimatedDuration establecido');
      } else {
        console.log('âš ️ No hay estimatedDuration en el appointment');
      }

      if (appointment.serviceType) {
        console.log('ðŸ”§ Estableciendo serviceType:', appointment.serviceType);
        setValue('serviceType', appointment.serviceType);
        console.log('âœ… serviceType establecido');
      } else {
        console.log('âš ️ No hay serviceType en el appointment');
      }

      if (appointment.notes) {
        console.log('ðŸ“ Estableciendo notes:', appointment.notes);
        setValue('notes', appointment.notes);
        console.log('âœ… notes establecido');
      } else {
        console.log('âš ️ No hay notes en el appointment');
      }

      if (appointment.mileage !== undefined && appointment.mileage !== null) {
        setValue('mileage', String(appointment.mileage));
      }

      if (appointment.oilChangeAndFilter !== undefined && appointment.oilChangeAndFilter !== null) {
        setValue('oilChangeAndFilter', Boolean(appointment.oilChangeAndFilter));
      }

      // Restaurar contacto de recepción en modo edición
      if (appointment.overrideContactPhone) {
        // Determinar si el override coincide con algún contacto conocido
        const overridePhone = appointment.overrideContactPhone;
        if (appointment.vehicleContactPhone && overridePhone === appointment.vehicleContactPhone) {
          setContactSource('vehicle');
        } else if (appointment.fleetContactPhone && overridePhone === appointment.fleetContactPhone) {
          setContactSource('fleet');
        } else if (appointment.clientPhone && overridePhone === appointment.clientPhone) {
          setContactSource('client');
        } else {
          setContactSource('custom');
          setCustomContactName(appointment.overrideContactName || '');
          setCustomContactPhone(appointment.overrideContactPhone || '');
        }
      }

      // Cargar servicios si existen - verificar tanto products como services
      console.log('ðŸ” Verificando productos del appointment...');
      console.log('ðŸ“¦ appointment.products:', appointment.products);
      console.log('ðŸ“¦ Tipo de appointment.products:', typeof appointment.products);
      console.log('ðŸ“¦ Array.isArray(appointment.products):', Array.isArray(appointment.products));

      const allProducts = [
        ...(appointment.products || [])
      ];

      console.log('ðŸ“¦ allProducts después del spread:', allProducts);
      console.log('ðŸ“¦ Longitud de allProducts:', allProducts.length);

      // 1) Intentar cargar desde appointment.services si existe
      const servicesFromField: any[] = Array.isArray((appointment as any).services) ? (appointment as any).services : [];
      if (servicesFromField.length > 0) {
        const mapped = servicesFromField.map((s: any) => ({
          id: s.id || `temp-${Date.now()}`,
          name: s.name || s.description || '',
          qty: s.quantity || s.qty || 1,
          sellingPrice: (s.price ?? s.sellingPrice ?? s.unitPrice ?? s.salePrice ?? 0),
          type: 'service',
          duration: s.duration || 60,
          requiredRole: s.requiredRole || '',
          requiredSkills: Array.isArray(s.requiredSkills)
            ? s.requiredSkills
            : (() => { try { return JSON.parse(s.requiredSkills || '[]'); } catch { return []; } })(),
          requiredStaff: s.requiredStaff || 1
        }));
        console.log('ðŸ”§ Estableciendo servicios desde appointment.services:', mapped);
        setSelectedServices(mapped);
      }

      if (allProducts.length > 0) {
        console.log('ðŸ“¦ Procesando productos/servicios del appointment:', allProducts);

        // Mostrar cada producto individualmente para debugging
        allProducts.forEach((item, index) => {
          console.log(`ðŸ“¦ Item ${index}:`, {
            id: item.id,
            name: item.name,
            type: item.type,
            quantity: item.quantity,
            price: item.price,
            duration: item.duration
          });
        });

        const servicesFromProducts = allProducts.filter((p: any) => (p.type === 'service' || p.__isService));
        const products = allProducts.filter((p: any) => p.type === 'product');

        // 2) Si aún no hay servicios en estado, intentar desde products
        if (servicesFromProducts.length > 0 && servicesFromField.length === 0) {
          const mappedServices = servicesFromProducts.map((s: any) => ({
            id: s.id || `temp-${Date.now()}`,
            name: s.name,
            qty: s.quantity || 1,
            sellingPrice: (s.price ?? s.sellingPrice ?? s.unitPrice ?? s.salePrice ?? 0),
            type: 'service',
            duration: s.duration || 60,
            requiredRole: s.requiredRole || '',
            requiredSkills: Array.isArray(s.requiredSkills)
              ? s.requiredSkills
              : (() => { try { return JSON.parse(s.requiredSkills || '[]'); } catch { return []; } })(),
            requiredStaff: s.requiredStaff || 1
          }));
          console.log('ðŸ”§ Estableciendo servicios desde products:', mappedServices);
          setSelectedServices(mappedServices);
        } else if (servicesFromProducts.length === 0 && servicesFromField.length === 0) {
          console.log('âš ️ No se encontraron servicios en products');
        }

        if (products.length > 0) {
          const mappedProducts = products.map((p: any) => ({
            id: p.id || `temp-${Date.now()}`,
            name: p.name,
            qty: p.quantity || 1,
            sellingPrice: (p.price ?? p.sellingPrice ?? p.salePrice ?? 0),
            type: 'product',
            location: p.location || '',
            stock: typeof p.stock !== 'undefined' ? p.stock : p.currentStock
          }));
          console.log('ðŸ“¦ Estableciendo productos:', mappedProducts);
          setSelectedProducts(mappedProducts);
        } else {
          console.log('âš ️ No se encontraron productos en los productos');
        }
      } else {
        console.log('âš ️ No hay productos o servicios en el appointment');
      }

      // 3) Fallback final: si aún no hay servicios en estado, inferir desde serviceType
      if ((servicesFromField.length === 0) && selectedServices.length === 0) {
        if (appointment.serviceType && appointment.serviceType.trim() && appointment.serviceType !== 'Servicio general') {
          console.log('ðŸ” Fallback: extrayendo servicios desde serviceType:', appointment.serviceType);
          const serviceNames = appointment.serviceType
            .split(',')
            .map((s: string) => s.trim())
            .filter((s: string) => s && s !== 'Servicio general');
          if (serviceNames.length > 0) {
            const extractedServices = serviceNames.map((name: string, index: number) => ({
              id: `extracted-${index}`,
              name,
              qty: 1,
              sellingPrice: (() => {
                const svc = services.find((x: any) => x.name === name);
                return (svc?.salePrice ?? 0);
              })(),
              type: 'service',
              duration: 60
            }));
            console.log('ðŸ”§ Estableciendo servicios desde serviceType:', extractedServices);
            setSelectedServices(extractedServices);
          }
        }
      }

      console.log('âœ… Formulario populado completamente');
      console.log('ðŸ” Estado final después de populateForm:', {
        selectedClient,
        clientId: watch('clientId'),
        vehicleId: watch('vehicleId'),
        scheduledDate: watch('scheduledDate'),
        timeSlot: watch('timeSlot')
      });
      console.log('ðŸ” selectedServices después de populateForm:', selectedServices);
      console.log('ðŸ” selectedProducts después de populateForm:', selectedProducts);
    } else if (mode === 'create' && initialData) {
      console.log('âž• Modo creación, usando initialData:', initialData);
      // Modo creación: usar datos iniciales
      if (initialData.scheduledDate) {
        setValue('scheduledDate', initialData.scheduledDate);
      }
      if (initialData.timeSlot) {
        setValue('timeSlot', initialData.timeSlot);
      }
      if (initialData.clientId) {
        setValue('clientId', initialData.clientId);
        fetchVehiclesByClient(initialData.clientId).then(() => {
          if (initialData.vehicleId) setValue('vehicleId', initialData.vehicleId);
        });
      }
      if (initialData.vehicleId) {
        setValue('vehicleId', initialData.vehicleId);
      }
      // Viene desde Presupuestos: queda elegido en el selector, y el efecto de
      // budgetId carga sus renglones y deja el turno enlazado
      if (initialData.budgetId) {
        setValue('budgetId', initialData.budgetId);
      }
    } else {
      console.log('âŒ No se puede poblar el formulario:', { mode, hasAppointment: !!appointment, hasInitialData: !!initialData });
    }
  };

  const onSubmit = async (data: any) => {
    try {
      console.log('ðŸ” Iniciando envío del turno...');
      console.log('ðŸ“‹ Datos del formulario:', data);
      console.log('ðŸ‘¥ Cliente seleccionado:', data.clientId);
      console.log('ðŸš— Vehículo seleccionado:', data.vehicleId);
      console.log('ðŸ“… Fecha programada:', data.scheduledDate);
      console.log('⏰ Horario:', data.timeSlot);
      console.log('ðŸ”§ Servicios seleccionados:', selectedServices);
      console.log('ðŸ“¦ Productos seleccionados:', selectedProducts);

      // Validaciones básicas
      if (!data.clientId) {
        toast.error('Debe seleccionar un cliente');
        return;
      }

      // El km no es obligatorio al crear el turno, ni el inmediato (21/09/2026):
      // si la OT queda sin km, el taller lo pide al completar el trabajo.

      // Para turnos inmediatos, no validar fecha y horario
      if (!isImmediate) {
        if (!data.scheduledDate) {
          toast.error('Debe seleccionar una fecha');
          return;
        }

        if (!data.timeSlot) {
          toast.error('Debe seleccionar un horario');
          return;
        }
      }

      // Validar que estimatedDuration sea un número válido
      if (!data.estimatedDuration || isNaN(parseInt(data.estimatedDuration))) {
        toast.error('La duración estimada debe ser un número válido');
        return;
      }

      if (selectedServices.length === 0 && selectedProducts.length === 0) {
        toast.error('Debe agregar al menos un servicio o producto');
        return;
      }

      const endpoint = mode === 'edit'
        ? `/api/appointments-enhanced/${appointment.id}`
        : '/api/appointments-enhanced';
      const method = mode === 'edit' ? 'put' : 'post';

      // Si es inmediato, usar fecha y hora actual
      const now = new Date();
      const currentDate = toYMD(now);
      const currentTime = now.toTimeString().slice(0, 5);

      // Limpiar vehicleId si está vacío o no hay vehículos
      const cleanData = { ...data };
      if (!cleanData.vehicleId || cleanData.vehicleId.trim() === '' || vehicles.length === 0) {
        delete cleanData.vehicleId;
      }

      // Crear serviceType basado en los servicios seleccionados (para compatibilidad)
      const serviceType = selectedServices.map(s => s.name).join(', ');

      // Crear items individuales para el remito - cada servicio en su propia fila
      const items: Array<{
        name: string;
        description: string;
        quantity: number;
        unitPrice: number;
        totalPrice: number;
        type: 'service' | 'product';
      }> = [];

      // Agregar cada servicio como item individual
      selectedServices.forEach(service => {
        items.push({
          name: service.name,
          description: service.name,
          quantity: service.qty,
          unitPrice: service.sellingPrice,
          totalPrice: service.sellingPrice * service.qty,
          type: 'service'
        });
      });

      // Agregar cada producto como item individual
      selectedProducts.forEach(product => {
        items.push({
          name: product.name,
          description: product.name,
          quantity: product.qty,
          unitPrice: product.sellingPrice,
          totalPrice: product.sellingPrice * product.qty,
          type: 'product'
        });
      });

      console.log('ðŸ“‹ Items creados para el turno:', items);
      console.log('ðŸ” Cantidad de servicios:', selectedServices.length);
      console.log('ðŸ” Cantidad de productos:', selectedProducts.length);
      console.log('ðŸ” Total de items:', items.length);

      const payload: any = {
        ...cleanData,
        // Para turnos inmediatos, usar fecha y hora actual
        scheduledDate: isImmediate ? currentDate : data.scheduledDate,
        timeSlot: isImmediate ? currentTime : data.timeSlot,
        // Al EDITAR no se toca el estado: mandarlo hacía retroceder a
        // "confirmado" un turno que ya estaba recepcionado o en proceso, y
        // desaparecía del Taller.
        ...(mode === 'edit' ? {} : { status: isImmediate ? 'in_progress' : 'confirmed' }),
        // El presupuesto elegido en el selector (venga de la URL o a mano) es el
        // que queda enlazado; si se destildó, el turno va sin presupuesto.
        // Al editar no se manda: el vínculo se cambia desde Presupuestos.
        ...(mode === 'edit' ? {} : { relatedBudgetId: data.budgetId || null }),
        stationKeys: stationOrder.length > 0 ? stationOrder : undefined,
        isReclamo: isReclamo,
        reclamoSourceId: reclamoSourceSelected?.id || null,
        reclamoType: isReclamo ? reclamoType : null,
        reclamoNotes: isReclamo ? reclamoNotes : null,
        // parseKm: '174.374' con punto de miles no se corta en 174
        mileage: parseKm(data.mileage) ?? undefined,
        oilChangeAndFilter: Boolean(data.oilChangeAndFilter),
        estimatedDuration: parseInt(data.estimatedDuration),
        serviceType: serviceType || 'Servicio general',
        items: items, // Agregar items individuales
        services: selectedServices.map(service => ({
          id: service.id,
          name: service.name,
          quantity: service.qty,
          price: service.sellingPrice,
          duration: service.duration || 60,
          type: service.type,
          requiredRole: service.requiredRole || '',
          requiredSkills: service.requiredSkills || [],
          requiredStaff: service.requiredStaff || 1
        })),
        products: selectedProducts.map(p => ({
          id: p.id,
          name: p.name,
          quantity: p.qty,
          price: p.sellingPrice,
          type: 'product',
          location: p.location || '',
          stock: typeof p.stock !== 'undefined' ? p.stock : undefined
        })),
        totalServiceCost: selectedServices.reduce((sum, s) => sum + (s.sellingPrice * s.qty), 0) +
          selectedProducts.reduce((sum, p) => sum + ((p.sellingPrice || 0) * p.qty), 0),
        resourceTypes: selectedResources // Agregar recursos seleccionados
      };

      // Agregar contacto de recepción (override) según la selección
      if (contactSource === 'vehicle' && vehicleContactPhone) {
        payload.overrideContactName = vehicleContactName;
        payload.overrideContactPhone = vehicleContactPhone;
      } else if (contactSource === 'fleet' && fleetContactPhone) {
        payload.overrideContactName = fleetContactName;
        payload.overrideContactPhone = fleetContactPhone;
      } else if (contactSource === 'client' && clientPhone) {
        const c = clients.find(c => c.id === data.clientId);
        payload.overrideContactName = c?.companyName || `${c?.firstName || ''} ${c?.lastName || ''}`.trim();
        payload.overrideContactPhone = clientPhone;
      } else if (contactSource === 'custom' && customContactPhone) {
        payload.overrideContactName = nombreCompletoContacto;
        payload.overrideContactPhone = customContactPhone;
      } else if (contactSource === 'other' && otherContact?.phone) {
        payload.overrideContactName = otherContact.name;
        payload.overrideContactPhone = otherContact.phone;
      }
      // Si contactSource === 'auto', no se envía override (usa cadena de fallback del backend)

      if (!Number.isInteger(payload.mileage)) {
        delete payload.mileage;
      }

      console.log('ðŸ“¤ Payload final a enviar:', payload);
      console.log('ðŸš€ Turno inmediato:', isImmediate);
      console.log('ðŸŒ Endpoint:', endpoint);
      console.log('ðŸ“¡ Método:', method);

      const response = await axios[method](endpoint, payload);

      // El contacto elegido (tipeado o tomado de otro vehículo del cliente)
      // queda guardado como contacto de ESTE vehículo para que la próxima vez
      // aparezca solo (best-effort: no bloquea la creación del turno).
      const contactoAGuardar =
        contactSource === 'custom' && customContactPhone
          ? { contactName: customContactName, contactPhone: customContactPhone }
          : contactSource === 'other' && otherContact?.phone
          ? { contactName: otherContact.name, contactPhone: otherContact.phone }
          : null;
      if (contactoAGuardar && payload.vehicleId) {
        try {
          await axios.patch(`/api/vehicles/${payload.vehicleId}/contact`, contactoAGuardar);
        } catch (e) {
          console.error('No se pudo guardar el contacto en el vehículo:', e);
        }
      }

      console.log('âœ… Respuesta del servidor:', response.data);
      console.log('âœ… Turno creado con ID:', response.data.appointment?.id);
      console.log('âœ… Estado del turno:', response.data.appointment?.status);

      const successMessage = isImmediate
        ? 'Turno inmediato creado y marcado como "En Progreso"'
        : (mode === 'edit' ? 'Turno actualizado exitosamente' : 'Turno creado exitosamente');

      toast.success(successMessage);

      // Solo resetear el formulario si no estamos editando
      if (mode !== 'edit') {
        reset();
        setSelectedServices([]);
        setSelectedProducts([]);
        setSelectedService('');
        setServiceQuantity(1);
        setIsImmediate(false);
      }

      // Esperar a que se complete onSuccess antes de cerrar
      await Promise.resolve(onSuccess());
      onClose();
    } catch (error: any) {
      console.error('âŒ Error guardando turno:', error);
      console.error('âŒ Detalles del error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });

      let errorMessage = 'Error al guardar el turno';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (Array.isArray(error.response?.data?.errors) && error.response.data.errors.length > 0) {
        // Errores de validación del backend (express-validator): mostrar el detalle
        errorMessage = error.response.data.errors.map((e: any) => e.msg).filter(Boolean).join(' · ');
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return 'Cliente, Vehículo y Horario';
      case 2: return 'Servicios y Detalles';
      default: return '';
    }
  };

  const vehicleCreateDialog = (
    <VehicleCreateDialog
      open={vehicleModalOpen}
      onOpenChange={setVehicleModalOpen}
      clientId={watchedClientId || selectedClient}
      onCreated={async (vehicle: any) => {
        const cid = watchedClientId || selectedClient;
        if (cid) await fetchVehiclesByClient(cid);
        if (vehicle?.id) setValue('vehicleId', vehicle.id);
      }}
    />
  );

  const clientCreateDialog = (
    <Dialog
      open={clientModalOpen}
      onOpenChange={(open) => {
        setClientModalOpen(open);
        if (!open) setClientForm({ firstName: '', lastName: '', phone: '', email: '' });
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Crear cliente</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="client-firstName">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                id="client-firstName"
                value={clientForm.firstName}
                onChange={(e) => setClientForm((c) => ({ ...c, firstName: e.target.value }))}
                placeholder="Juan"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-lastName">Apellido</Label>
              <Input
                id="client-lastName"
                value={clientForm.lastName}
                onChange={(e) => setClientForm((c) => ({ ...c, lastName: e.target.value }))}
                placeholder="Pérez"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="client-phone">Teléfono</Label>
            <Input
              id="client-phone"
              value={clientForm.phone}
              onChange={(e) => setClientForm((c) => ({ ...c, phone: e.target.value }))}
              placeholder="3416858698"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="client-email">Email</Label>
            <Input
              id="client-email"
              type="email"
              value={clientForm.email}
              onChange={(e) => setClientForm((c) => ({ ...c, email: e.target.value }))}
              placeholder="cliente@email.com"
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => setClientModalOpen(false)}
            disabled={clientSaving}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={handleCreateClient} disabled={clientSaving}>
            {clientSaving ? 'Creando...' : 'Crear cliente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const nextStep = () => {
    if (step < 2) {
      // Preservar datos del formulario al cambiar de paso
      const currentData = {
        clientId: watch('clientId'),
        vehicleId: watch('vehicleId'),
        scheduledDate: watch('scheduledDate'),
        timeSlot: watch('timeSlot'),
        estimatedDuration: watch('estimatedDuration'),
        serviceType: watch('serviceType'),
        notes: watch('notes'),
        mileage: watch('mileage')
      };

      setStep(step + 1);

      // Restaurar datos después del cambio de paso
      setTimeout(() => {
        if (currentData.clientId) setValue('clientId' as any, currentData.clientId);
        if (currentData.vehicleId) setValue('vehicleId' as any, currentData.vehicleId);
        if (currentData.scheduledDate) setValue('scheduledDate' as any, currentData.scheduledDate);
        if (currentData.timeSlot) setValue('timeSlot' as any, currentData.timeSlot);
        if (currentData.estimatedDuration) setValue('estimatedDuration' as any, currentData.estimatedDuration);
        if (currentData.serviceType) setValue('serviceType' as any, currentData.serviceType);
        if (currentData.notes) setValue('notes' as any, currentData.notes);
        if (currentData.mileage) setValue('mileage' as any, currentData.mileage);
      }, 50);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      // Preservar datos del formulario al cambiar de paso
      const currentData = {
        clientId: watch('clientId'),
        vehicleId: watch('vehicleId'),
        scheduledDate: watch('scheduledDate'),
        timeSlot: watch('timeSlot'),
        estimatedDuration: watch('estimatedDuration'),
        serviceType: watch('serviceType'),
        notes: watch('notes'),
        mileage: watch('mileage')
      };

      setStep(step - 1);

      // Restaurar datos después del cambio de paso
      setTimeout(() => {
        if (currentData.clientId) setValue('clientId' as any, currentData.clientId);
        if (currentData.vehicleId) setValue('vehicleId' as any, currentData.vehicleId);
        if (currentData.scheduledDate) setValue('scheduledDate' as any, currentData.scheduledDate);
        if (currentData.timeSlot) setValue('timeSlot' as any, currentData.timeSlot);
        if (currentData.estimatedDuration) setValue('estimatedDuration' as any, currentData.estimatedDuration);
        if (currentData.serviceType) setValue('serviceType' as any, currentData.serviceType);
        if (currentData.notes) setValue('notes' as any, currentData.notes);
        if (currentData.mileage) setValue('mileage' as any, currentData.mileage);
      }, 50);
    }
  };

  if (!asPage && !isOpen) return null;

  // Preparar el objeto cliente seleccionado de forma segura
  const clientFound = clients.find(c => c.id === watchedClientId);
  const selectedClientProp = clientFound ? {
    id: watchedClientId,
    firstName: clientFound.firstName || '',
    lastName: clientFound.lastName || '',
    companyName: clientFound.companyName,
    type: clientFound.companyName ? 'company' : 'individual' as 'company' | 'individual',
    email: clientFound.email,
    phone: clientFound.phone
  } : null;

  // OJO: esto NO puede ser un componente definido inline (const FormShell = () => ...).
  // Un componente definido dentro del render crea un TIPO nuevo en cada render, y React
  // desmonta/remonta TODO el subarbol (el form, el Select de vehiculos, sus estados y el
  // foco) en cada tecla. Eso rompia la carga de vehiculos ("Cargando vehiculos..." pegado)
  // y hacia imposible tipear al crear uno. Como funcion helper, React reconcilia el mismo
  // Dialog entre renders y no remonta nada.
  const renderShell = (children: React.ReactNode) => {
    if (asPage) {
      return (
        <div className="mx-auto w-full max-w-4xl rounded-3xl border border-border/60 bg-card shadow-sm">
          <div className="rounded-3xl">{children}</div>
        </div>
      );
    }

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden rounded-3xl border-0 shadow-2xl p-0">
          {children}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {renderShell(<>
          <div className="p-6 pb-0">
            {asPage ? (
              <h2 className="text-xl font-semibold">
                {mode === 'edit' ? 'Editar Turno' : 'Nuevo Turno'}
              </h2>
            ) : (
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">
                  {mode === 'edit' ? 'Editar Turno' : 'Nuevo Turno'}
                </DialogTitle>
              </DialogHeader>
            )}
          </div>

          {/* Progress Bar */}
          <div className="px-6 py-2">
            <Progress value={step === 1 ? 50 : 100} className="h-2" />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
            <div className={cn(
              "flex-1 px-6 pb-3",
              asPage ? "" : "overflow-y-auto max-h-[calc(90vh-180px)]"
            )}>
              {/* Step 1: Cliente, Vehículo y Horario */}
              {step === 1 && (
                <div className="space-y-4">
                  {/* Sección Cliente y Vehículo */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Cliente */}
                    <div className="space-y-2">
                      <Label htmlFor="clientId" className="font-medium text-sm">
                        <UserIcon className="h-4 w-4 inline mr-2" />
                        Cliente *
                      </Label>
                      <ClientCombobox
                        clients={clients.map(c => ({
                          id: c.id,
                          firstName: c.firstName,
                          lastName: c.lastName,
                          companyName: c.companyName,
                          type: c.companyName ? 'company' : 'individual',
                          email: c.email,
                          phone: c.phone
                        }))}
                        selectedClient={selectedClientProp}
                        onSelect={(client: { id: string; firstName?: string; lastName?: string; companyName?: string; type: 'individual' | 'company'; email?: string; phone?: string } | null) => {
                          try {
                            if (client) {
                              console.log('ðŸ” DEBUG: Cliente seleccionado:', client);
                              setValue('clientId', client.id);
                              setSelectedClient(client.id);
                              // Las llamadas a fetchVehiclesByClient y fetchBudgetsByClient
                              // se ejecutarán automáticamente a través del useEffect cuando cambie watchedClientId
                            } else {
                              setValue('clientId', '');
                              setSelectedClient('');
                              setVehicles([]);
                              setBudgets([]);
                            }
                          } catch (error) {
                            console.error('âŒ Error al seleccionar cliente:', error);
                            toast.error('Error al seleccionar el cliente. Por favor, intenta nuevamente.');
                          }
                        }}
                        placeholder="Buscar cliente..."
                        getClientName={(client) => client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Sin nombre'}
                        onCreateClient={(query) => {
                          // Pre-cargar el nombre con lo que escribió en la búsqueda
                          const parts = query.trim().split(/\s+/);
                          setClientForm({
                            firstName: parts[0] || '',
                            lastName: parts.slice(1).join(' '),
                            phone: '',
                            email: ''
                          });
                          setClientModalOpen(true);
                        }}
                      />
                      <ClientQuickPicks
                        onConsumidorFinal={() => { setValue('clientId', 'consumidor-final', { shouldValidate: true, shouldDirty: true }); setSelectedClient('consumidor-final'); }}
                        onPlateResolved={({ client, vehicle }) => {
                          setClients((prev: any[]) => prev.some((c) => c.id === client.id)
                            ? prev
                            : [...prev, { id: client.id, firstName: client.name, lastName: '', companyName: '', email: '', phone: client.phone || '' }]);
                          // shouldValidate: si ya había un "Cliente es requerido" en
                          // pantalla, se borra al resolver la patente.
                          setValue('clientId', client.id, { shouldValidate: true, shouldDirty: true });
                          setSelectedClient(client.id);
                          if (vehicle?.id) {
                            // La lista de vehículos del cliente todavía no cargó: se
                            // guarda el elegido y el efecto de `vehicles` lo re-aplica
                            // cuando llega la lista.
                            pendingVehicleIdRef.current = vehicle.id;
                            setValue('vehicleId', vehicle.id, { shouldValidate: true, shouldDirty: true });
                          }
                        }}
                      />
                      <input
                        type="hidden"
                        {...register('clientId', { required: 'Cliente es requerido' })}
                      />
                      {errors.clientId && (
                        <p className="text-sm text-destructive">{errors.clientId.message as string}</p>
                      )}
                    </div>

                    {/* Vehículo */}
                    <div className="space-y-2">
                      <Label htmlFor="vehicleId" className="font-medium text-sm">
                        Vehículo
                      </Label>
                      <Select
                        key={`veh-${vehicles.map((v) => v.id).join(',')}`}
                        value={watch('vehicleId') || ''}
                        onValueChange={(value) => {
                          if (value === '__create__') {
                            setVehicleModalOpen(true);
                            return;
                          }
                          setValue('vehicleId', value);
                        }}
                        disabled={!watchedClientId}
                      >
                        <SelectTrigger id="vehicleId">
                          <SelectValue placeholder={
                            !watchedClientId
                              ? 'Primero selecciona un cliente'
                              : vehiclesLoading
                                ? 'Cargando vehículos...'
                                : (vehicles.length > 0 ? 'Seleccionar vehículo...' : 'Sin vehículos — creá uno')
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__create__">Crear vehículo...</SelectItem>
                          {vehicles.map((vehicle) => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              {vehicle.licensePlate} - {vehicle.make} {vehicle.model} {vehicle.year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.vehicleId && (
                        <p className="text-sm text-destructive">{errors.vehicleId.message as string}</p>
                      )}
                    </div>
                  </div>

                  {/* Contacto de Recepción */}
                  {watchedVehicleId && (
                    <div className="space-y-3 p-3 border border-border rounded-lg bg-muted/30">
                      <Label className="font-medium text-sm">
                        <PhoneIcon className="h-4 w-4 inline mr-2" />
                        Contacto de recepción (para mensajes WP)
                      </Label>
                      <div className="space-y-2">
                        {/* Si hay un solo número cargado (y nada para elegir de otros
                            vehículos), no mostrar la elección: solo el contacto */}
                        {receptionContact.single && otherVehicleContacts.length === 0 && (
                          <div className="flex items-center gap-3 p-2 rounded-md bg-muted/40 border border-border">
                            <div className="flex-1">
                              <span className="text-sm font-medium">{receptionContact.single.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">{receptionContact.single.phone}</span>
                            </div>
                          </div>
                        )}
                        {/* Elección de contacto (si hay más de un número disponible) */}
                        {(!receptionContact.single || otherVehicleContacts.length > 0) && (<>
                        {/* Opción: Automático */}
                        <label className={cn(
                          "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                          contactSource === 'auto' ? "bg-primary/10 border border-primary/40" : "hover:bg-muted/50"
                        )}>
                          <input
                            type="radio"
                            name="contactSource"
                            value="auto"
                            checked={contactSource === 'auto'}
                            onChange={() => setContactSource('auto')}
                            className="accent-blue-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium">Automático</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              (usa el contacto del vehículo, flota o cliente)
                            </span>
                          </div>
                        </label>

                        {/* Opción: Contacto del vehículo */}
                        {vehicleContactPhone && (
                          <label className={cn(
                            "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                            contactSource === 'vehicle' ? "bg-primary/10 border border-primary/40" : "hover:bg-muted/50"
                          )}>
                            <input
                              type="radio"
                              name="contactSource"
                              value="vehicle"
                              checked={contactSource === 'vehicle'}
                              onChange={() => setContactSource('vehicle')}
                              className="accent-blue-500"
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium">{vehicleContactName || 'Operario del vehículo'}</span>
                              <span className="text-xs text-muted-foreground ml-2">{vehicleContactPhone}</span>
                            </div>
                          </label>
                        )}

                        {/* Opciones: contactos ya cargados en otros vehículos del cliente */}
                        {otherVehicleContacts.map((c) => (
                          <label key={c.phone} className={cn(
                            "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                            contactSource === 'other' && otherContact?.phone === c.phone
                              ? "bg-primary/10 border border-primary/40" : "hover:bg-muted/50"
                          )}>
                            <input
                              type="radio"
                              name="contactSource"
                              value={`other-${c.phone}`}
                              checked={contactSource === 'other' && otherContact?.phone === c.phone}
                              onChange={() => { setContactSource('other'); setOtherContact({ name: c.name, phone: c.phone }); }}
                              className="accent-blue-500"
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium">{c.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">{c.phone}</span>
                              {c.plate && <span className="text-xs text-muted-foreground/70 ml-2">(usado en {c.plate})</span>}
                            </div>
                          </label>
                        ))}

                        {/* Opción: Contacto de flota */}
                        {fleetContactPhone && (
                          <label className={cn(
                            "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                            contactSource === 'fleet' ? "bg-primary/10 border border-primary/40" : "hover:bg-muted/50"
                          )}>
                            <input
                              type="radio"
                              name="contactSource"
                              value="fleet"
                              checked={contactSource === 'fleet'}
                              onChange={() => setContactSource('fleet')}
                              className="accent-blue-500"
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium">{fleetContactName || 'Contacto de flota'}</span>
                              <span className="text-xs text-muted-foreground ml-2">{fleetContactPhone}</span>
                            </div>
                          </label>
                        )}

                        {/* Opción: Cliente directo */}
                        {clientPhone && (
                          <label className={cn(
                            "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                            contactSource === 'client' ? "bg-primary/10 border border-primary/40" : "hover:bg-muted/50"
                          )}>
                            <input
                              type="radio"
                              name="contactSource"
                              value="client"
                              checked={contactSource === 'client'}
                              onChange={() => setContactSource('client')}
                              className="accent-blue-500"
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium">
                                {(() => {
                                  const c = clients.find(c => c.id === watchedClientId);
                                  return c?.companyName || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || 'Cliente';
                                })()}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">{clientPhone}</span>
                            </div>
                          </label>
                        )}

                        </>)}
                        {/* Opción: Otro contacto (siempre disponible) */}
                        <label className={cn(
                          "flex items-start gap-3 p-2 rounded-md cursor-pointer transition-colors",
                          contactSource === 'custom' ? "bg-primary/10 border border-primary/40" : "hover:bg-muted/50"
                        )}>
                          <input
                            type={receptionContact.single ? 'checkbox' : 'radio'}
                            name="contactSource"
                            value="custom"
                            checked={contactSource === 'custom'}
                            onChange={(e) => setContactSource(receptionContact.single ? (e.target.checked ? 'custom' : 'auto') : 'custom')}
                            className="accent-blue-500 mt-1"
                          />
                          <div className="flex-1 space-y-2">
                            <span className="text-sm font-medium">Otro contacto</span>
                            {contactSource === 'custom' && (
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  placeholder="Nombre"
                                  value={customContactName}
                                  onChange={(e) => setCustomContactName(e.target.value)}
                                  className="text-sm"
                                />
                                <Input
                                  placeholder="Apellido"
                                  value={customContactLastName}
                                  onChange={(e) => setCustomContactLastName(e.target.value)}
                                  className="text-sm"
                                />
                                <Input
                                  placeholder="Cargo (ej: encargado de flota)"
                                  value={customContactRole}
                                  onChange={(e) => setCustomContactRole(e.target.value)}
                                  className="text-sm"
                                />
                                <Input
                                  placeholder="Teléfono"
                                  value={customContactPhone}
                                  onChange={(e) => setCustomContactPhone(e.target.value)}
                                  className="text-sm"
                                />
                              </div>
                            )}
                          </div>
                        </label>
                      </div>

                      {/* Botón para guardar contacto en el vehículo */}
                      {contactSource === 'custom' && customContactPhone && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          disabled={savingVehicleContact}
                          onClick={async () => {
                            try {
                              setSavingVehicleContact(true);
                              await axios.patch(`/api/vehicles/${watchedVehicleId}/contact`, {
                                contactName: customContactName,
                                contactLastName: customContactLastName,
                                contactRole: customContactRole,
                                contactPhone: customContactPhone
                              });
                              setVehicleContactName(nombreCompletoContacto);
                              setVehicleContactPhone(customContactPhone);
                              toast.success('Contacto guardado en el vehículo');
                            } catch (err) {
                              toast.error('Error guardando contacto');
                            } finally {
                              setSavingVehicleContact(false);
                            }
                          }}
                        >
                          {savingVehicleContact ? 'Guardando...' : 'Guardar este contacto para este vehículo'}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Selección de Presupuesto */}
                  {selectedClient && (
                    <div className="space-y-2">
                      <Label htmlFor="budgetId" className="font-medium text-sm">
                        <DocumentTextIcon className="h-4 w-4 inline mr-2" />
                        Presupuesto (Opcional)
                      </Label>
                      <Select
                        value={watch('budgetId') ? watch('budgetId') : 'none'}
                        onValueChange={(value) => setValue('budgetId', value === 'none' ? '' : value)}
                        disabled={mode === 'edit'}
                      >
                        <SelectTrigger id="budgetId">
                          <SelectValue placeholder="Sin presupuesto - Crear turno manual" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin presupuesto - Crear turno manual</SelectItem>
                          {(budgetElegido && !budgets.some((b) => b.id === budgetElegido.id) ? [...budgets, budgetElegido] : budgets).map((budget) => (
                            <SelectItem key={budget.id} value={budget.id}>
                              {etiquetaPresupuesto(budget)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {mode === 'edit'
                          ? 'El presupuesto enlazado se cambia desde Presupuestos (al aprobar, "Enlazar a un turno abierto")'
                          : 'Con un presupuesto elegido se cargan sus servicios y productos, la duración estimada, y el turno queda enlazado a ese presupuesto'}
                      </p>
                    </div>
                  )}

                  {/* "Áreas de Servicio Requeridas" se ocultó: ahora se determina automáticamente
                      a partir del campo workshopStationKey de cada producto/servicio cargado.
                      La lógica interna selectedResources sigue para el cálculo de slots del calendario. */}

                  {/* Sección Fecha y Horario */}
                  <div className="space-y-6">
                    {/* Selección de fecha o turno inmediato */}
                    <div className="space-y-3">
                      <Label className="font-medium text-sm">
                        Fecha del Servicio
                      </Label>
                      <div className="flex gap-3">
                        <DatePickerAppointment
                          id="scheduledDateCompact"
                          value={isImmediate ? '' : watch('scheduledDate')}
                          onChange={(date) => {
                            setValue('scheduledDate', date);
                            setIsImmediate(false);
                          }}
                          minDate={hoyLocal()}
                          compact={true}
                        />
                        <Tip label="El turno se creará para el momento actual y se marcará como 'En Progreso'">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsImmediate(true);
                              setValue('scheduledDate', '');
                              setValue('timeSlot', '');
                            }}
                            className={cn(
                              "h-8 rounded-lg px-3 text-sm font-medium transition-colors",
                              isImmediate
                                ? "!bg-blue-600 !text-white hover:!bg-blue-700"
                                : "bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                            )}
                          >
                            <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                            Inmediato
                          </Button>
                        </Tip>
                      </div>
                    </div>

                    {/* Campo oculto para validación de fecha */}
                    <input
                      type="hidden"
                      {...register('scheduledDate', { required: !isImmediate ? 'Fecha es requerida' : false })}
                    />
                    {errors.scheduledDate && !isImmediate && (
                      <p className="text-sm text-destructive">{errors.scheduledDate.message as string}</p>
                    )}

                    {/* Horarios Disponibles - Solo mostrar si no es inmediato */}
                    {!isImmediate && watchedDate && (
                      <div className="space-y-3">
                        <Label className="font-medium text-sm">Horarios Disponibles</Label>
                        <p className="text-sm text-muted-foreground">
                          La duración se calculará automáticamente según los servicios seleccionados
                        </p>
                        {loadingSlots ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          </div>
                        ) : (slotsAll.length === 0 && availableSlots.length === 0) ? (
                          <div className="flex items-center justify-center py-8 border border-dashed rounded-lg border-muted-foreground/20">
                            <p className="text-sm text-muted-foreground">No hay horarios para esta fecha</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {/* Semáforo sin bloqueos: todos los horarios se pueden elegir.
                                verde=libre · amarillo=sobreturno · rojo=lleno (se agenda igual) */}
                            {(slotsAll.length > 0
                              ? slotsAll
                              : availableSlots.map(t => ({ time: t, nivel: 'libre' as const, ocupados: 0, capacidad: 0 }))
                            ).map((s) => {
                              const slot = s.time;
                              const elegido = watch('timeSlot') === slot;
                              const hasOverbooking = s.nivel !== 'libre';

                              return (
                                <TooltipProvider key={slot}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <label
                                        className={cn(
                                          "relative inline-flex items-center justify-center h-8 px-3 border rounded-lg cursor-pointer transition-colors text-sm font-medium gap-1",
                                          elegido
                                            ? "!bg-blue-600 !text-white border-blue-600 hover:!bg-blue-700"
                                            : s.nivel === 'lleno'
                                              ? "bg-red-50 text-red-700 border-red-300 hover:bg-red-100 dark:bg-red-950/60 dark:text-red-300 dark:border-red-800"
                                              : hasOverbooking
                                                ? "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-700"
                                                : "bg-transparent text-gray-700 border-border hover:bg-muted/50 dark:text-gray-300"
                                        )}
                                      >
                                        <input
                                          type="radio"
                                          {...register('timeSlot', { required: !isImmediate ? 'Horario es requerido' : false })}
                                          value={slot}
                                          className="sr-only"
                                        />
                                        {hasOverbooking && !elegido && (
                                          <AlertTriangle className="h-3 w-3" />
                                        )}
                                        <span>{slot}</span>
                                      </label>
                                    </TooltipTrigger>
                                    {hasOverbooking && (
                                      <TooltipContent>
                                        <p className="text-xs">
                                          {s.nivel === 'lleno' ? 'Horario lleno' : 'Sobreturno'}: {s.ocupados}/{s.capacidad} ocupado — se puede agendar igual
                                        </p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            })}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Verde libre · amarillo sobreturno · rojo lleno — todos se pueden elegir igual.
                          </p>
                          </div>
                        )}
                        {errors.timeSlot && (
                          <p className="text-sm text-destructive">{errors.timeSlot.message as string}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Servicios del Presupuesto Seleccionado */}
                  {selectedBudget && selectedServices.length > 0 && (
                    <div className={`border rounded-lg p-4 transition-colors duration-200 ${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'}`}>
                      <h4 className={`font-medium mb-2 transition-colors duration-200 ${isDarkMode ? 'text-green-300' : 'text-green-900'}`}>Servicios del Presupuesto</h4>
                      <div className="space-y-2">
                        {selectedServices.map((service, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className={`transition-colors duration-200 ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
                              {service.qty}x {service.name}
                            </span>
                            <span className={`font-medium transition-colors duration-200 ${isDarkMode ? 'text-green-200' : 'text-green-700'}`}>
                              ${((service.sellingPrice || 0) * service.qty).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        <div className={`border-t pt-2 mt-2 transition-colors duration-200 ${isDarkMode ? 'border-green-600' : 'border-green-300'}`}>
                          <div className={`flex justify-between font-medium transition-colors duration-200 ${isDarkMode ? 'text-green-300' : 'text-green-900'}`}>
                            <span>Total estimado:</span>
                            <span>
                              ${selectedServices.reduce((sum, s) => sum + ((s.sellingPrice || 0) * s.qty), 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Detalles del Servicio */}
              {step === 2 && (
                <div className="space-y-8">
                  {/* Campos ocultos del paso 2 */}
                  <input type="hidden" {...register('serviceType')} value={selectedServices.length > 0 ? selectedServices.map(s => s.name).join(', ') : 'Servicio general'} />
                  <input type="hidden" {...register('estimatedDuration')} />

                  {/* Servicios Manuales - Solo cuando NO hay presupuesto */}
                  {!selectedBudget && (
                    <div className="space-y-4">
                      <Label className="text-lg font-semibold">
                        Productos y Servicios
                      </Label>

                      {/* Buscador de productos/servicios con filtrado por texto */}
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Agrega los servicios y productos que se realizarán en este turno. El tiempo se calculará automáticamente.
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Atajos: <kbd className="px-1 rounded border bg-muted">.</kbd> abrir buscador ·
                          {' '}<kbd className="px-1 rounded border bg-muted">↑</kbd>/<kbd className="px-1 rounded border bg-muted">↓</kbd> mover ·
                          {' '}<kbd className="px-1 rounded border bg-muted">*</kbd> editar cantidad ·
                          {' '}<kbd className="px-1 rounded border bg-muted">Supr</kbd> eliminar
                        </p>
                        <div className="relative">
                          {(() => {
                            // Lista plana de items para navegar con teclado
                            const q = productPickerQuery.trim().toLowerCase();
                            const filteredServices = services.filter(s => !q || (s.name || '').toLowerCase().includes(q));
                            const filteredProducts = catalogProducts.filter(p => !q || (p.name || '').toLowerCase().includes(q));
                            const flatItems: Array<{ kind: 'service' | 'product'; data: any }> = [
                              ...filteredServices.map(s => ({ kind: 'service' as const, data: s })),
                              ...filteredProducts.map(p => ({ kind: 'product' as const, data: p }))
                            ];

                            const addService = (service: any) => {
                              const existingIdx = selectedServices.findIndex(s => s.id === service.id);
                              let updated: BudgetItem[];
                              if (existingIdx >= 0) {
                                updated = [...selectedServices];
                                updated[existingIdx] = { ...updated[existingIdx], qty: updated[existingIdx].qty + 1 };
                              } else {
                                updated = [...selectedServices, {
                                  id: service.id, name: service.name, qty: 1,
                                  sellingPrice: service.salePrice, type: 'service',
                                  duration: (service as any).duration ?? (service as any).estimatedDuration ?? 60
                                }];
                              }
                              setSelectedServices(updated);
                              const totalDur = updated.reduce((t, it) => t + (it.duration || 60) * it.qty, 0);
                              if (!durationTouchedRef.current) setValue('estimatedDuration', Math.max(totalDur, 30));
                              setValue('serviceType', updated.map(s => s.name).join(', ') || 'Servicio general');
                              toast.success(`Servicio "${service.name}" agregado`);
                              setProductPickerQuery(''); setProductPickerOpen(false);
                            };

                            const addProduct = (product: any) => {
                              const existingIdx = selectedProducts.findIndex(p => p.id === product.id);
                              if (existingIdx >= 0) {
                                const updated = [...selectedProducts];
                                updated[existingIdx] = { ...updated[existingIdx], qty: updated[existingIdx].qty + 1 };
                                setSelectedProducts(updated);
                              } else {
                                setSelectedProducts([...selectedProducts, {
                                  id: product.id, name: product.name, qty: 1,
                                  sellingPrice: product.sellingPrice || (product as any).salePrice || 0,
                                  type: 'product'
                                }]);
                              }
                              toast.success(`Producto "${product.name}" agregado`);
                              setProductPickerQuery(''); setProductPickerOpen(false);
                            };

                            const addAtIndex = (idx: number) => {
                              const item = flatItems[idx];
                              if (!item) return;
                              if (item.kind === 'service') addService(item.data);
                              else addProduct(item.data);
                            };

                            const handleInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (!productPickerOpen) {
                                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') setProductPickerOpen(true);
                                return;
                              }
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setProductPickerHighlight((h) => Math.min(flatItems.length - 1, h + 1));
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setProductPickerHighlight((h) => Math.max(0, h - 1));
                              } else if (e.key === 'Enter') {
                                if (flatItems.length === 0) return;
                                e.preventDefault();
                                const idx = Math.min(Math.max(productPickerHighlight, 0), flatItems.length - 1);
                                addAtIndex(idx);
                              } else if (e.key === 'Escape') {
                                setProductPickerOpen(false);
                              }
                            };

                            // Auto-scroll del item highlighted hacia la vista
                            // (se ejecuta en el render, sin useEffect porque depende de los flatItems)
                            setTimeout(() => {
                              const list = productPickerListRef.current;
                              if (!list) return;
                              const node = list.querySelector(`[data-picker-idx="${productPickerHighlight}"]`) as HTMLElement | null;
                              if (node) {
                                const top = node.offsetTop;
                                const bottom = top + node.offsetHeight;
                                if (top < list.scrollTop) list.scrollTop = top;
                                else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
                              }
                            }, 0);

                            const renderItem = (item: { kind: 'service' | 'product'; data: any }, idx: number) => {
                              const isHl = idx === productPickerHighlight;
                              const isService = item.kind === 'service';
                              const d: any = item.data;
                              return (
                                <div
                                  key={`${item.kind}-${d.id}`}
                                  data-picker-idx={idx}
                                  onMouseEnter={() => setProductPickerHighlight(idx)}
                                  onMouseDown={(e) => { e.preventDefault(); addAtIndex(idx); }}
                                  className={cn(
                                    "flex items-center justify-between px-3 py-2 text-sm cursor-pointer border-l-2",
                                    isHl
                                      ? "bg-primary/15 text-foreground border-l-primary"
                                      : "border-l-transparent hover:bg-accent/50"
                                  )}
                                >
                                  <span className="flex-1 truncate text-foreground">{d.name}</span>
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    ${isService ? d.salePrice : (d.sellingPrice || d.salePrice || 0)}
                                    {isService ? ` · ${(d.duration ?? d.estimatedDuration ?? 60)}min` : ''}
                                  </span>
                                </div>
                              );
                            };

                            // Renderizar el input + dropdown
                            return (
                              <>
                                <input
                                  ref={productPickerInputRef}
                                  type="text"
                                  placeholder="Buscar producto o servicio..."
                                  value={productPickerQuery}
                                  onFocus={() => setProductPickerOpen(true)}
                                  onChange={(e) => {
                                    setProductPickerQuery(e.target.value);
                                    setProductPickerOpen(true);
                                  }}
                                  onBlur={() => setTimeout(() => setProductPickerOpen(false), 200)}
                                  onKeyDown={handleInputKey}
                                  className="w-full px-3 py-2 text-base border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                />
                                {productPickerOpen && (
                                  <div ref={productPickerListRef} className="absolute z-50 mt-1 w-full max-h-[300px] overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                                    {flatItems.length === 0 ? (
                                      <div className="px-3 py-4 text-sm text-muted-foreground text-center">No se encontraron coincidencias</div>
                                    ) : (
                                      <>
                                        {filteredServices.length > 0 && (
                                          <div>
                                            <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/40">Servicios</div>
                                            {flatItems.slice(0, filteredServices.length).map((it, i) => renderItem(it, i))}
                                          </div>
                                        )}
                                        {filteredProducts.length > 0 && (
                                          <div>
                                            <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/40">Productos</div>
                                            {flatItems.slice(filteredServices.length).map((it, i) => renderItem(it, i + filteredServices.length))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Tabla de items agregados */}
                      {(selectedServices.length > 0 || selectedProducts.length > 0) ? (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="hover:bg-muted/50 border-b">
                                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Producto/Servicio</TableHead>
                                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Ubicacion</TableHead>
                                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-right">Cantidad</TableHead>
                                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-right">Acciones</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedServices.map((s, idx) => (
                                <TableRow
                                  key={`svc-${idx}`}
                                  onClick={() => setFocusedItemIndex(idx)}
                                  className={cn(
                                    "border-b hover:bg-muted/50 cursor-pointer",
                                    focusedItemIndex === idx && "bg-primary/10 ring-1 ring-primary/40"
                                  )}
                                >
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                                    <div className="font-medium">{s.name}</div>
                                  </TableCell>
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                                    <span className="text-xs text-muted-foreground">Servicio</span>
                                  </TableCell>
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                                    <span className="text-xs text-muted-foreground">-</span>
                                  </TableCell>
                                  <Tip label="Doble click para editar cantidad">
                                    <TableCell
                                      className="py-1 px-2 align-middle whitespace-nowrap text-right cursor-pointer"
                                      onDoubleClick={() => startEditingQuantity(idx, 'service', s.qty)}
                                    >
                                    {editingQuantityIndex === idx && editingQuantityType === 'service' ? (
                                      <div className="flex items-center justify-end gap-2">
                                        <Input
                                          type="number"
                                          min="1"
                                          value={editingQuantityValue}
                                          onChange={(e) => setEditingQuantityValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              saveQuantity(idx, 'service');
                                            } else if (e.key === 'Escape') {
                                              cancelEditingQuantity();
                                            }
                                          }}
                                          className="w-20 h-8"
                                          autoFocus
                                        />
                                        <Tip label="Guardar">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => saveQuantity(idx, 'service')}
                                            className="h-6 w-6 text-green-600 hover:text-green-700"
                                          >
                                            <CheckIcon className="h-3 w-3" />
                                          </Button>
                                        </Tip>
                                        <Tip label="Cancelar">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={cancelEditingQuantity}
                                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                          >
                                            <XMarkIcon className="h-3 w-3" />
                                          </Button>
                                        </Tip>
                                      </div>
                                    ) : (
                                      <span className="font-medium">{s.qty}</span>
                                    )}
                                    </TableCell>
                                  </Tip>
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removeManualService(idx)}
                                      className="h-8 w-8 text-destructive hover:text-destructive"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {selectedProducts.map((p, idx) => {
                                const globalIdx = selectedServices.length + idx;
                                return (
                                <TableRow
                                  key={`prd-${idx}`}
                                  onClick={() => setFocusedItemIndex(globalIdx)}
                                  className={cn(
                                    "border-b hover:bg-muted/50 cursor-pointer",
                                    focusedItemIndex === globalIdx && "bg-primary/10 ring-1 ring-primary/40"
                                  )}
                                >
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                                    <div className="font-medium">{p.name}</div>
                                  </TableCell>
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                                    <span className="text-xs text-muted-foreground">Producto</span>
                                  </TableCell>
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                                    <span className="text-xs text-muted-foreground">
                                      {p.stock > 0 && p.location ? p.location : '-'}
                                    </span>
                                  </TableCell>
                                  <Tip label="Doble click para editar cantidad">
                                    <TableCell
                                      className="py-1 px-2 align-middle whitespace-nowrap text-right cursor-pointer"
                                      onDoubleClick={() => startEditingQuantity(idx, 'product', p.qty)}
                                    >
                                    {editingQuantityIndex === idx && editingQuantityType === 'product' ? (
                                      <div className="flex items-center justify-end gap-2">
                                        <Input
                                          type="number"
                                          min="1"
                                          value={editingQuantityValue}
                                          onChange={(e) => setEditingQuantityValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              saveQuantity(idx, 'product');
                                            } else if (e.key === 'Escape') {
                                              cancelEditingQuantity();
                                            }
                                          }}
                                          className="w-20 h-8"
                                          autoFocus
                                        />
                                        <Tip label="Guardar">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => saveQuantity(idx, 'product')}
                                            className="h-6 w-6 text-green-600 hover:text-green-700"
                                          >
                                            <CheckIcon className="h-3 w-3" />
                                          </Button>
                                        </Tip>
                                        <Tip label="Cancelar">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={cancelEditingQuantity}
                                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                          >
                                            <XMarkIcon className="h-3 w-3" />
                                          </Button>
                                        </Tip>
                                      </div>
                                    ) : (
                                      <span className="font-medium">{p.qty}</span>
                                    )}
                                    </TableCell>
                                  </Tip>
                                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setSelectedProducts(prev => prev.filter((_, i) => i !== idx))}
                                      className="h-8 w-8 text-destructive hover:text-destructive"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground border rounded-lg">
                          <p>No hay productos o servicios agregados</p>
                          <p className="text-xs mt-1">Selecciona un producto o servicio para agregarlo</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Resumen cuando HAY presupuesto */}
                  {selectedBudget && selectedServices.length > 0 && (
                    <div className={`border rounded-lg p-6 transition-colors duration-200 ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                      <h3 className={`text-lg font-medium mb-4 border-b pb-2 transition-colors duration-200 ${isDarkMode ? 'text-blue-300 border-blue-600' : 'text-blue-900 border-blue-300'}`}>
                        Resumen del Turno
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className={`font-medium transition-colors duration-200 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Servicios incluidos:</span>
                          <p className={`transition-colors duration-200 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>{selectedServices.length} servicios del presupuesto</p>
                        </div>
                        <div>
                          <span className={`font-medium transition-colors duration-200 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Tiempo estimado:</span>
                          <p className={`transition-colors duration-200 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>{selectedServices.reduce((sum, s) => sum + ((s.duration || 60) * s.qty), 0)} minutos</p>
                        </div>
                        <div>
                          <span className={`font-medium transition-colors duration-200 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Total estimado:</span>
                          <p className={`transition-colors duration-200 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>${selectedServices.reduce((sum, s) => sum + ((s.sellingPrice || 0) * s.qty), 0).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Orden de estaciones — solo si hay más de una detectada */}
                  {stationOrder.length > 1 && (
                    <div className="rounded-lg border border-border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Orden de estaciones</span>
                        <span className="text-xs text-muted-foreground">Arrastrá para reordenar</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {stationOrder.map((key, idx) => {
                          const st = availableStations.find(s => s.key === key);
                          return (
                            <div key={key} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-muted border border-border text-sm">
                              <span className="text-muted-foreground font-mono text-xs">{idx + 1}.</span>
                              <span className="font-medium">{st?.name || key}</span>
                              <div className="flex flex-col ml-1 gap-0.5">
                                <button type="button" disabled={idx === 0}
                                  onClick={() => { const arr = [...stationOrder]; [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]]; setStationOrder(arr); }}
                                  className="text-muted-foreground hover:text-foreground disabled:opacity-20 leading-none text-[10px]">▲</button>
                                <button type="button" disabled={idx === stationOrder.length - 1}
                                  onClick={() => { const arr = [...stationOrder]; [arr[idx], arr[idx+1]] = [arr[idx+1], arr[idx]]; setStationOrder(arr); }}
                                  className="text-muted-foreground hover:text-foreground disabled:opacity-20 leading-none text-[10px]">▼</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        El vehículo pasará por las estaciones en este orden.
                      </p>
                    </div>
                  )}

                  {/* Duración estimada (sugerencia auto + ajuste manual) */}
                  <div className="rounded-lg border border-border p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium flex items-center gap-1.5">
                        <ClockIcon className="h-4 w-4 text-muted-foreground" /> Duración estimada
                      </label>
                      <button type="button" onClick={recalcAutoDuration} className="text-xs text-primary hover:underline">
                        Recalcular auto
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={15}
                        max={1440}
                        className="h-8 w-24"
                        value={durationInput}
                        onChange={e => {
                          durationTouchedRef.current = true;
                          setDurationInput(e.target.value);
                        }}
                        onBlur={commitDuration}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitDuration(); } }}
                      />
                      <span className="text-sm text-muted-foreground">
                        min{Number(durationInput) > 0 ? ` · ${fmtDurationHM(Number(durationInput))}` : ''}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Se calcula sumando los servicios. Ajustala si algunos comparten pasos (ej. balanceo y desarme usan el mismo sacar/poner rueda).
                    </p>
                  </div>

                  {/* Toggle Reclamo */}
                  <div className={`rounded-lg border p-3 space-y-3 ${isReclamo ? 'border-red-500/40 bg-red-500/5' : 'border-border'}`}>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isReclamo}
                        onChange={e => { setIsReclamo(e.target.checked); if (!e.target.checked) { setReclamoSourceSelected(null); setReclamoSourceSearch(''); setReclamoType(''); setReclamoNotes(''); } }}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-red-600 dark:text-red-400 flex items-center gap-1.5"><ExclamationTriangleIcon className="h-4 w-4" /> Es un reclamo sobre un trabajo anterior</span>
                    </label>
                    {isReclamo && (
                      <div className="space-y-2 pl-5">
                        {/* Buscar OT original */}
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">OT original (opcional)</label>
                          {reclamoSourceSelected ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-bold text-red-600">{reclamoSourceSelected.code}</span>
                              <button type="button" onClick={() => { setReclamoSourceSelected(null); setReclamoSourceSearch(''); }} className="text-xs text-muted-foreground hover:text-red-500">× cambiar</button>
                            </div>
                          ) : (
                            <div className="relative">
                              <input
                                type="text"
                                value={reclamoSourceSearch}
                                onChange={async e => {
                                  setReclamoSourceSearch(e.target.value);
                                  if (e.target.value.length >= 2) {
                                    try {
                                      const res = await (await import('axios')).default.get(`/api/appointments-enhanced?search=${e.target.value}&limit=10`);
                                      setReclamoSourceResults(res.data.appointments || []);
                                    } catch { setReclamoSourceResults([]); }
                                  } else { setReclamoSourceResults([]); }
                                }}
                                placeholder="Buscar por código OT o cliente..."
                                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                              />
                              {reclamoSourceResults.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 rounded-md border border-border bg-popover shadow-lg max-h-[140px] overflow-y-auto">
                                  {reclamoSourceResults.map((a: any) => (
                                    <button key={a.id} type="button"
                                      onMouseDown={e => e.preventDefault()}
                                      onClick={() => { setReclamoSourceSelected({ id: a.id, code: a.appointmentCode }); setReclamoSourceSearch(a.appointmentCode); setReclamoSourceResults([]); }}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2">
                                      <span className="font-mono font-bold">{a.appointmentCode}</span>
                                      <span className="text-muted-foreground truncate">{a.clientName || a.serviceType}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Tipo de reclamo */}
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Tipo de reclamo</label>
                          <select value={reclamoType} onChange={e => setReclamoType(e.target.value)}
                            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm">
                            <option value="">Seleccionar...</option>
                            <option value="trabajo_mal_realizado">Trabajo mal realizado</option>
                            <option value="pieza_defectuosa">Pieza defectuosa</option>
                            <option value="problema_no_detectado">Problema no detectado</option>
                            <option value="danio_durante_servicio">Daño durante el servicio</option>
                            <option value="otro">Otro</option>
                          </select>
                        </div>
                        {/* Descripción */}
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Descripción</label>
                          <textarea value={reclamoNotes} onChange={e => setReclamoNotes(e.target.value)}
                            placeholder="Describí el problema que reporta el cliente..."
                            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm resize-none min-h-[60px]"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botón de Notas del Servicio */}
                  <div className="flex justify-start">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowNotesModal(true)}
                      className="h-8 rounded-lg px-3 text-sm font-medium"
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      Notas del Servicio
                      {watch('notes') && (
                        <span className="ml-2 text-xs text-muted-foreground">({watch('notes').length} caracteres)</span>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>

          <DialogFooter className="px-6 py-2">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
              >
                Anterior
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            {step < 2 ? (
              <Button
                type="button"
                onClick={nextStep}
                disabled={!watchedClientId}
                className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] h-9 px-4 py-2 ${isDarkMode
                  ? 'bg-white text-gray-900 hover:bg-gray-100'
                  : 'bg-black text-white hover:bg-black/90'
                  }`}
              >
                Siguiente
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] h-9 px-4 py-2 ${isDarkMode
                  ? 'bg-white text-gray-900 hover:bg-gray-100'
                  : 'bg-black text-white hover:bg-black/90'
                  }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </div>
                ) : (
                  isImmediate ? 'Crear Turno Inmediato' : (mode === 'edit' ? 'Actualizar Turno' : 'Crear Turno')
                )}
              </Button>
            )}
          </DialogFooter>
      </>)}

      {vehicleCreateDialog}
      {clientCreateDialog}

      {/* Modal de Notas del Servicio */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Notas del Servicio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notes-modal" className="font-medium text-sm">Notas</Label>
              <Textarea
                id="notes-modal"
                {...register('notes')}
                rows={6}
                placeholder="Descripción del trabajo, aclaraciones para el cliente, etc."
                className="min-h-[150px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowNotesModal(false)}
            >
              Cerrar
            </Button>
            <Button
              type="button"
              onClick={() => setShowNotesModal(false)}
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] h-9 px-4 py-2 ${isDarkMode
                ? 'bg-white text-gray-900 hover:bg-gray-100'
                : 'bg-black text-white hover:bg-black/90'
                }`}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppointmentForm; 

